/**
 * td-sticker-preview.js
 *
 * Live sticker preview for td-sticker-builder. One module, two paths,
 * one renderer:
 *
 *   Transparent uploads → local silhouette → POST /api/trace → render
 *   Opaque uploads      → Cloudinary e_background_removal → poll → POST /api/trace → render
 *
 * Confidence gate (perim-chroma + erosion-solidity, from R4) runs CLIENT-side
 * before any server call. If the matte is unsafe (busy bg, retained halo, etc.)
 * we fall back to honest-flat — /api/trace is never invoked.
 *
 * Tracing itself is done server-side by real potrace (Round-14 architecture):
 *   briefs/sticker-preview-b3-round14-potrace-server-2026-06-13.md
 * The hand-rolled marching-squares + Schneider Bézier tracer (R10-R13) has
 * been removed in favor of /api/trace returning smooth SVG path d-strings
 * for Thin / Normal / Wide bleed widths in one call.
 *
 * Fidelity rule (hard): the trace produces ONLY the cutline + white border.
 * The art inside is always the customer's uploaded pixels. Never recolored
 * or re-rendered.
 *
 * Public API: window.TDStickerPreview.init({ pane, controls, onStateChange })
 *   returns { setFile(File), clear(), getMeta() } or null if no canvas.
 */
(function () {
  'use strict';

  // ────────────────────────────────────────────────────────────
  // Config
  // ────────────────────────────────────────────────────────────

  var CLOUDINARY = {
    cloud:     'dsbz1mtmr',
    preset:    'tapedudes_unsigned',
    uploadUrl: 'https://api.cloudinary.com/v1_1/dsbz1mtmr/image/upload',
    matteBase: 'https://res.cloudinary.com/dsbz1mtmr/image/upload',
    // w_1400,c_limit shrinks the delivered matte to ≤1400px longest side
    // (c_limit never upscales). Cuts mobile canvas memory + bandwidth; server
    // /api/trace fetches its own copy by cloudinaryId regardless.
    transform: 'e_background_removal,f_png,q_auto,w_1400,c_limit',
    // Vector rasterization (PDF/AI/EPS). pg_1 limits multi-page PDFs to page 1.
    //   vectorTransform         — transparent-vector path: no bg-removal,
    //                             alpha already present, silhouette traceable.
    //   vectorKnockoutTransform — opaque-vector path: Canva and Illustrator
    //                             "export PDF" both ship opaque-white backgrounds,
    //                             which is the dominant case for this ICP. Without
    //                             bg-removal the cut shape becomes a white square.
    //                             AI bg-removal (not e_make_transparent) — the
    //                             latter color-keys all near-white globally and
    //                             holes out interior whites (letter interiors,
    //                             highlights). Fired only when the no-bg pass
    //                             returns no alpha, so transparent vectors pay
    //                             zero add-on cost.
    vectorTransform:         'f_png,pg_1,q_auto,w_1400,c_limit',
    vectorKnockoutTransform: 'e_background_removal,f_png,pg_1,q_auto,w_1400,c_limit',
    maxBytes:  10 * 1024 * 1024,          // 10 MB client-side cap (preset can't enforce)
    pollMs:    [800, 1200, 1500, 2000, 2500, 3000, 4000, 4000], // ~21s total
  };

  // Mobile canvas-memory fix (Round 15). Every pixel-reading step (alpha
  // probe, silhouette, extractAlpha, PNG encode) used to allocate a canvas
  // at the upload's full resolution. Mobile Safari/Chrome silently fail
  // getImageData/drawImage past ~12 MP — the gate then reads an empty
  // buffer ("no alpha"), falls through to flat, and /api/trace is never
  // called. Cap all analysis canvases here. The art drawn into the final
  // preview is still the customer's state.img / matte at display scale —
  // these working canvases are analysis-only inputs (the gate downsamples
  // to 512 after this; the server downsamples to 1024).
  var WORKING_MAX_CLIENT = 1400;

  // Server-side potrace endpoint. Same Vercel project as the checkout bridge.
  var TRACE_ENDPOINT = 'https://tapedudes-checkout-bridge.vercel.app/api/trace';

  // Confidence gate parameters. These survive from R4 — they decide whether
  // /api/trace is worth calling at all. (R10-R13 client-side trace knobs are
  // gone; potrace owns trace quality now.)
  var GATE = {
    confThreshold:        0.55,
    erosionPx:            3,
    erosionPenalty:       0.45,
    erosionDropMax:       0.25,
    perimBandPx:          4,
    perimChromaStdMax:    0.08,
    perimChromaPenalty:   0.50,
    perimChromaCleanFloor:0.03,
    matteTrimPx:          2,       // R5 alpha-fringe trim (Cloudinary path only)
    gateMaxDim:           512,     // downscale silhouette before gate maths — gate doesn't need fidelity
  };

  var CANVAS_SIZE = 960;     // logical pixel size; backing is CSS × DPR (R10)
  var SHAPE_PROP  = 0.72;    // geometric shape occupies this fraction of canvas
  var CUTLINE = {
    color: 'rgba(15, 23, 42, 0.85)',
    dash:  [9, 6],
    width: 1.5,
  };

  // M1 Brief 2 — material finish for holographic / chrome / glitter.
  //
  // Two layers per animated frame:
  //   (1) Material backing  — fills the die-cut (bleed) area with the
  //       finish gradient at full opacity, source-over, with the same drop
  //       shadow the white bleed used to have. This makes the border + the
  //       transparent-art regions read as the specialty material. (Fix A
  //       from the Brief 2 revision — `overlay`/`screen` on white are
  //       identity, so the original brief left the border plain white.)
  //   (2) Face sheen        — drawn on top of the customer's art, also
  //       clipped to the die-cut path, using source-over blends at moderate
  //       alphas so the finish reads on BOTH light and dark art. (Fix B
  //       from the revision — the original `overlay` is near-identity on
  //       bright colors, so a yellow logo on holo came out plain yellow.)
  //
  // A8 fidelity rule still holds: the art pixels are never replaced or
  // recolored. With SHEEN_ENABLED = false both passes are skipped and the
  // render is byte-identical to the vinyl render of the same file — that's
  // the A8 compliance proof and the kill-switch. The die-cut border itself
  // IS the material ("ink on film"), not the customer's art, so coloring
  // the border is parallel to the Clear-substrate exception.
  var SHEEN_ENABLED = true;
  // Frame budget for the animation tick. ~30fps is plenty for a slow shimmer
  // and halves battery cost vs 60fps; rAF still fires at the display's native
  // rate, the paint is gated.
  var SHEEN_FRAME_MS = 33;
  // Per-material config — Dev-tuning surface. Starting values; visually tuned.
  var SHEEN = {
    holographic: {
      cycleMs:      5500,   // one full rainbow scroll cycle
      tintAlpha:    0.24,   // source-over rainbow tint over art — visible on light + dark
      overlayAlpha: 0.35,   // overlay accent — boosts mid-tone iridescence
    },
    chrome: {
      cycleMs:        4200, // one full specular-streak travel
      bandAlpha:      0.32, // source-over vertical metal banding — visible on bright art
      specularAlpha:  0.60, // source-over white streak — visible on any art
    },
    glitter: {
      twinkleMs:        2400,    // one full sine cycle per dot (procedural fallback)
      densityPxPerDot:  700,     // ~1 dot per N logical px² (procedural fallback)
      dotMinPx:         1,
      dotMaxPx:         3.2,
      baseAlphaMin:     0.45,    // raised from 0.30 — sparkles need to read on bright art
      baseAlphaMax:     0.95,
      backingColor:     '#f3f3f5', // soft off-white — fallback if backgroundUrl is missing
      // Owner-supplied tileable glitter assets:
      //   Background PNG (opaque RGB) → drawn ONCE, cover-scaled inside the
      //     die-cut path (no tile repeat). Single-draw eliminates the
      //     brightness-block seams that the prior createPattern('repeat')
      //     approach showed in the pre-trace flat square (2026-06-14 fix).
      //   Pattern PNG (mostly-transparent RGBA, v2) → tiled + drifted as the
      //     animated sparkle face pass on top of the customer's art. The
      //     pattern's mostly-transparent flecks don't expose tile-block seams.
      patternAlpha:     0.85,    // global alpha applied to the pattern fill
      patternScale:     0.45,    // pattern tile scale (1.0 = native; lower = more sparkle density)
      patternDriftMs:   8000,    // ms per full slow drift cycle (owner pref 2026-06-14: 4000 → 6000 → 8000)
    },
  };
  // Holographic rainbow palette. First/last colors are identical so the
  // gradient wraps without a seam when the phase scrolls through.
  var HOLO_PALETTE = ['#ff3df5', '#3dafff', '#3dff7a', '#fff03d', '#ff7a3d', '#a93dff', '#ff3df5'];

  // ────────────────────────────────────────────────────────────
  // Module-scope refs (one builder per page)
  // ────────────────────────────────────────────────────────────

  var pane, canvas, ctx, controls, emptyEl, noteEl, statusEl;
  var state;
  var onStateChange = null;
  var pollHandle = null;
  var uploadAbort = null;
  var traceAbort = null;

  // ── Stage stepper UI (Brief 2026-06-18) ──
  // The stepper replaces the single-line status text with an animated card
  // showing one step per applicable async stage for the current upload.
  // Stage advancement is event-driven (real setPhase() calls from the pipeline
  // branches) — never timed. The mockup at docs/specs/sticker-preview-loader-
  // mockup.html is the visual target; its demo timer is presentation-only.
  var stepperEl, stepperStepsEl, stepperTrackfillEl, stepperLabelEl, stepperSublabelEl;
  // Canonical order; setPhase respects this when inserting incrementally.
  var STAGE_ORDER = ['prepare', 'bg', 'trace'];
  // Inline SVG icons (Tabler-style line glyphs). The Tabler webfont isn't
  // loaded on the sticker template, so paths are inlined and tinted via
  // CSS `currentColor`.
  var SVG_CLOUD_UPLOAD =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"' +
    ' stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M7 18a4.6 4.4 0 0 1 0-9 5 4.5 0 0 1 11 2h1a3.5 3.5 0 0 1 0 7H7" />' +
    '<polyline points="9 15 12 12 15 15" />' +
    '<line x1="12" y1="12" x2="12" y2="21" />' +
    '</svg>';
  var SVG_WAND =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"' +
    ' stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M6 21l15-15-3-3L3 18z" />' +
    '<line x1="15" y1="6" x2="18" y2="9" />' +
    '<path d="M9 3v4M11 5h-4M19 13v4M21 15h-4" />' +
    '</svg>';
  var SVG_SCISSORS =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"' +
    ' stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<circle cx="6" cy="6" r="3" />' +
    '<circle cx="6" cy="18" r="3" />' +
    '<line x1="20" y1="4" x2="8.12" y2="15.88" />' +
    '<line x1="14.47" y1="14.48" x2="20" y2="20" />' +
    '<line x1="8.12" y1="8.12" x2="12" y2="12" />' +
    '</svg>';
  var SVG_CHECK =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"' +
    ' stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<polyline points="20 6 9 17 4 12" />' +
    '</svg>';
  var STAGE_META = {
    prepare: { icon: SVG_CLOUD_UPLOAD, label: 'Preparing preview',    sub: 'Uploading your artwork'   },
    bg:      { icon: SVG_WAND,         label: 'Removing background',  sub: 'Isolating your design'    },
    trace:   { icon: SVG_SCISSORS,     label: 'Tracing cutline',      sub: 'Finding the die-cut edge' },
  };
  var DONE_META = { icon: SVG_CHECK,   label: 'Preview ready',        sub: 'Your die-cut is traced'   };
  // Visible duration of the "Preview ready" success state before the stepper
  // tears down and the traced canvas reveals. Long enough to feel like a
  // resolution beat, short enough to not bore the user. ms.
  var DONE_HOLD_MS = 1500;
  var stepper = {
    active:        false,    // true between first setPhase() and resetStepper()
    stages:        [],       // ordered subset of STAGE_ORDER currently rendered
    current:       null,     // current phase key, or 'done', or null
    doneHideTimer: null,     // setTimeout handle for the done → reveal pass
  };

  // Material identity for this PDP. Set once in init() from opts.material
  // (which the builder threads through from state.product_type). Renderer
  // branches on this: Clear (Brief 1) is a fill-opacity branch on the bleed;
  // Holographic / Chrome / Glitter (Brief 2) are additive sheen overlays
  // drawn on top of the customer's pixels inside the cut path. Mapping is
  // centralized in normalizeMaterial() so every renderer sees the same enum.
  // Default 'vinyl' preserves the pre-Brief-1 behavior when material is
  // absent (contract is backward-compatible).
  var material = 'vinyl';
  function normalizeMaterial(raw) {
    switch (raw) {
      case 'clear':       return 'clear';
      case 'holographic': return 'holographic';
      case 'chrome':      return 'chrome';
      case 'glitter':     return 'glitter';
      // vinyl/bumper/qr_code/sheets → no material treatment (vinyl renderer).
      default:            return 'vinyl';
    }
  }
  function materialIsAnimated(m) {
    return m === 'holographic' || m === 'chrome' || m === 'glitter';
  }

  // M1 Brief 2 — sheen animation loop state (module-scope; one preview per page).
  // sheenLoop is null when stopped; {handle, startTs, lastPaintTs} when running.
  // The loop is started after the first render of an animated material, and
  // stopped on clear()/setFile()/reduced-motion-toggle. It self-throttles paints
  // when document.hidden or the canvas is detached (modal closed).
  var sheenLoop = null;
  var reducedMotionMql = null;
  var reducedMotionWired = false;

  // Glitter texture PNGs (owner-supplied tileables, owner-approved 2026-06-14).
  // Two assets are threaded via init():
  //   glitterBackgroundUrl → the opaque substrate; tiled as the die-cut
  //     backing in materialBackingFill(). Falls back to backingColor solid
  //     fill while loading / if missing — glitter PDPs never render blank.
  //   glitterPatternUrl    → the mostly-transparent sparkle PNG; tiled +
  //     drifted as the animated face pass in drawGlitterFace(). Falls back
  //     to the procedural dot field while loading / if missing.
  // Both load lazily, async, once per page.
  var glitterBackgroundUrl    = null;
  var glitterBackgroundImg    = null;
  var glitterBackgroundState  = 'idle'; // 'idle' | 'loading' | 'ready' | 'failed'
  var glitterPatternUrl       = null;
  var glitterPatternImg       = null;
  var glitterPatternState     = 'idle';

  function freshState() {
    return {
      file:            null,
      img:             null,    // HTMLImageElement of original upload
      matteImg:        null,    // canvas/Image used for trace input AND inside-clip art (Cloudinary path)
      paths:           null,    // { thin:{d}, normal:{d}, wide:{d}, w, h } from /api/trace
      conf:            null,
      why:             '',
      decision:        'empty', // 'empty'|'pending'|'flat'|'contour'|'geometric'|'unrenderable'
      used_cloudinary: false,
      public_id:       null,
      secure_url:      null,
      // M1 Brief 2 (revised) — sheen animation cache. sheenCtx is the per-
      // frame info paintMaterialFrame() needs to redraw the full dynamic
      // stack (bg + material backing + art + face sheen + cutline) at the
      // current phase. glitterField is the cached procedural dot field
      // (deterministic seed per upload + region size). Both cleared on
      // every freshState(). The original brief proposed an offscreen base
      // canvas; the revision drops it — the shadow has to be cast by the
      // material backing fill itself, so the base would have to be empty.
      sheenCtx:        null,    // { kind, backingPath, clipPath, facePath, cutlinePath, regionX/Y/W/H, artSrc, artX/Y/W/H }
      glitterField:    null,    // { key, dots: [{x,y,r,base,phaseOff}] }
      glitterSeed:     1,       // deterministic per upload (file name + size)
      controls: {
        shape: 'custom',  // 'custom' | 'circle' | 'square' | 'rect'
        bleed: 0.05,      // fraction of canvas (Thin 0.025, Normal 0.05, Wide 0.08)
        round: 0.07,      // fraction of shape min-side (geo only)
        fit:   'fit',     // 'fit' | 'fill' (geo only)
        bg:    'none',    // 'none' for stripe backdrop, or any CSS color
      },
    };
  }

  function bleedKey(b) {
    if (b <= 0.03) return 'thin';
    if (b >= 0.07) return 'wide';
    return 'normal';
  }

  // ────────────────────────────────────────────────────────────
  // M1 Brief 2 — Material sheen overlays (holographic/chrome/glitter)
  //
  // A8 fidelity rule: the sheen is an ADDITIVE BLEND on top of the
  // customer's unchanged art pixels, clipped to the cut path. Art pixels
  // are never replaced, recolored, or restyled — the kill-switch
  // (SHEEN_ENABLED = false) must produce a render byte-identical to the
  // vinyl render of the same file.
  //
  // The animation loop caches the static base (bg + bleed + art) into
  // an offscreen canvas after the first render; each subsequent frame
  // = blit base → sheen pass → cutline. Loop is throttled to ~30fps and
  // self-pauses paints when document.hidden or the canvas is not in the
  // DOM flow (modal closed). Reduced-motion → static frame, no loop.
  // ────────────────────────────────────────────────────────────

  function prefersReducedMotion() {
    if (reducedMotionMql) return !!reducedMotionMql.matches;
    if (!window.matchMedia) return false;
    return !!window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function setupReducedMotionListener() {
    if (reducedMotionWired) return;
    if (!window.matchMedia) return;
    reducedMotionMql = window.matchMedia('(prefers-reduced-motion: reduce)');
    var onChange = function () {
      debug('reduced-motion', { matches: !!reducedMotionMql.matches });
      if (reducedMotionMql.matches) {
        stopSheenLoop();
      } else if (materialIsAnimated(material) && state && state.img) {
        // Resume: redraw a base + start the loop. render() will rebuild
        // state.baseCanvas + state.sheenCtx and then call startSheenLoop().
        render();
      }
    };
    if (reducedMotionMql.addEventListener) {
      reducedMotionMql.addEventListener('change', onChange);
    } else if (reducedMotionMql.addListener) {
      reducedMotionMql.addListener(onChange);
    }
    reducedMotionWired = true;
  }

  // Deterministic seed from File so the glitter field is stable for the same
  // upload across re-renders / resize / modal reopen.
  function hashSeed(file) {
    var s = (file && file.name != null) ? (file.name + ':' + (file.size | 0)) : 'default';
    var h = 0;
    for (var i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
    return (h >>> 0) || 1;
  }

  // Tiny seeded PRNG — Mulberry32. Deterministic, 32-bit state.
  function mulberry32(seed) {
    var s = (seed >>> 0) || 1;
    return function () {
      s = (s + 0x6D2B79F5) | 0;
      var t = s;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  // Glitter background PNG loader. Triggered from init() for glitter SKUs.
  // If it fails, materialBackingFill() falls back to backingColor solid fill.
  function loadGlitterBackground() {
    if (!glitterBackgroundUrl) return;
    if (glitterBackgroundState !== 'idle') return;
    glitterBackgroundState = 'loading';
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      glitterBackgroundImg = img;
      glitterBackgroundState = 'ready';
      debug('glitterBackground', { state: 'ready', w: img.naturalWidth, h: img.naturalHeight });
      // If a frame is already on-screen, repaint so the texture replaces the
      // solid-color fallback immediately.
      if (material === 'glitter' && state && state.img && state.sheenCtx && !sheenLoop) {
        paintMaterialFrame(state.sheenCtx, 0);
      }
    };
    img.onerror = function () {
      glitterBackgroundState = 'failed';
      debug('glitterBackground', { state: 'failed', url: glitterBackgroundUrl });
    };
    img.src = glitterBackgroundUrl;
  }

  // Glitter PNG pattern loader. Triggered from init() when the SKU is glitter
  // and a URL was threaded through. One image per page; loaded lazily, async.
  // If the load fails or 404s, glitterPatternState transitions to 'failed'
  // and drawGlitterFace() keeps using the procedural dot field forever — no
  // visual glitch, no console spam beyond a single debug() line.
  function loadGlitterPattern() {
    if (!glitterPatternUrl) return;
    if (glitterPatternState !== 'idle') return;
    glitterPatternState = 'loading';
    var img = new Image();
    // crossOrigin keeps the canvas untainted so the col-3 thumbnail's
    // toDataURL() call in td-sticker-builder.js continues to work.
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      glitterPatternImg = img;
      glitterPatternState = 'ready';
      debug('glitterPattern', { state: 'ready', w: img.naturalWidth, h: img.naturalHeight });
      // If a glitter render is already on-screen, redraw so the pattern
      // replaces the procedural fallback immediately.
      if (material === 'glitter' && state && state.img && state.sheenCtx) {
        if (sheenLoop) {
          // Loop will pick up the pattern on the next tick automatically.
        } else {
          // Reduced-motion / static frame — repaint once at phase 0.
          paintMaterialFrame(state.sheenCtx, 0);
        }
      }
    };
    img.onerror = function () {
      glitterPatternState = 'failed';
      debug('glitterPattern', { state: 'failed', url: glitterPatternUrl });
    };
    img.src = glitterPatternUrl;
  }

  function ensureGlitterField(seed, region) {
    var key = seed + ':' + Math.round(region.w) + 'x' + Math.round(region.h);
    if (state.glitterField && state.glitterField.key === key) return;
    var rng = mulberry32(seed);
    var density = SHEEN.glitter.densityPxPerDot;
    var count = Math.max(80, Math.floor((region.w * region.h) / density));
    var rMin = SHEEN.glitter.dotMinPx;
    var rMax = SHEEN.glitter.dotMaxPx;
    var aMin = SHEEN.glitter.baseAlphaMin;
    var aMax = SHEEN.glitter.baseAlphaMax;
    var dots = new Array(count);
    for (var i = 0; i < count; i++) {
      dots[i] = {
        x: region.x + rng() * region.w,
        y: region.y + rng() * region.h,
        r: rMin + rng() * (rMax - rMin),
        base: aMin + rng() * (aMax - aMin),
        phaseOff: rng() * Math.PI * 2,
      };
    }
    state.glitterField = { key: key, dots: dots };
  }

  // Brief 2 revision — full per-frame redraw of the animated stack. The
  // brief originally proposed a "bg + shadow" offscreen base cache; we drop
  // it because the drop shadow is created BY the material backing's fill
  // operation, not a standalone draw — caching shadow without fill would
  // require fill-with-shadow-then-erase-inside gymnastics. Per-frame cost
  // (1 bg fill + 1 shadowed backing fill + 1 drawImage + 1–2 sheen fills +
  // 1 cutline stroke) is well within the 30fps budget on mobile.
  //
  // The dynamic stack, in order:
  //   1. Clear + bg
  //   2. Material backing fill on s.backingPath, WITH the same drop shadow
  //      the white bleed used to cast (so the sticker still floats)
  //   3. Customer art, clipped to s.clipPath, drawn on top of the backing
  //   4. Face sheen (source-over blends) clipped to s.facePath — visible on
  //      bright AND dark art (the Brief 2 revision fix)
  //   5. Dashed cutline on s.cutlinePath (if present — flat has none)
  function paintMaterialFrame(s, phaseMs) {
    if (!s || !canvas || !ctx) return;
    var S = CANVAS_SIZE;

    // Reset transform for the clear, then restore the logical-coords mapping
    // that drawCustomContour / drawGeometric / drawFlat are written against.
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    ctx.setTransform(canvas.width / S, 0, 0, canvas.height / S, 0, 0);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // 1. Background (preview stripe / chosen bg color)
    if (state.controls.bg && state.controls.bg !== 'none') {
      ctx.fillStyle = state.controls.bg;
      ctx.fillRect(0, 0, S, S);
    }

    // 2. Material backing — fills the bleed area with the finish.
    //    Same shadow params as the original white-bleed render so the sticker
    //    keeps casting the same drop shadow against the page.
    ctx.save();
    ctx.shadowColor   = 'rgba(0,0,0,0.30)';
    ctx.shadowBlur    = 26;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 10;
    ctx.fillStyle     = materialBackingFill(s.regionX, s.regionY, s.regionW, s.regionH, phaseMs);
    ctx.fill(s.backingPath);
    ctx.restore();

    // 2b. Glitter substrate cover overlay — replaces the prior
    //     createPattern('repeat') backing, which showed visible brightness-
    //     block seams in the pre-trace flat square (the bigger the tile
    //     grid, the more seams). A single cover-scaled drawImage has no
    //     internal tile boundaries, so no seams in any state. The shadow
    //     was already cast by the solid-color fill above; this overlay
    //     does NOT use shadow params so the silhouette shadow isn't
    //     double-rendered or clipped. While the PNG is loading or has
    //     failed, the solid backingColor fill stays visible as the fallback.
    if (material === 'glitter' && glitterBackgroundState === 'ready' && glitterBackgroundImg) {
      ctx.save();
      ctx.clip(s.backingPath);
      drawImageCover(glitterBackgroundImg, s.regionX, s.regionY, s.regionW, s.regionH);
      ctx.restore();
    }

    // 3. Customer art on top, clipped to the cut path. Pixels are the
    //    customer's exact upload — never recolored (A8 fidelity rule).
    if (s.artSrc) {
      ctx.save();
      ctx.clip(s.clipPath);
      ctx.drawImage(s.artSrc, s.artX, s.artY, s.artW, s.artH);
      ctx.restore();
    }

    // 4. Face sheen — source-over passes tuned to read on bright AND dark
    //    art. Clipped to facePath (which spans both border and face for
    //    contour and geometric; the art-rect for flat).
    drawFaceSheen(s.facePath, s.regionX, s.regionY, s.regionW, s.regionH, phaseMs);

    // 5. Dashed cutline overlay (skipped for flat fallback).
    if (s.cutlinePath) {
      ctx.save();
      ctx.strokeStyle = CUTLINE.color;
      ctx.setLineDash(CUTLINE.dash);
      ctx.lineWidth = CUTLINE.width;
      ctx.stroke(s.cutlinePath);
      ctx.restore();
    }
  }

  // canvas.offsetParent is null when the canvas or any ancestor is
  // display:none — that's how the preview modal hides the engine. We use
  // it instead of a builder-side notify so Brief 2 stays engine-only.
  function isCanvasOnscreen() {
    if (!canvas) return false;
    if (document.hidden) return false;
    return canvas.offsetParent !== null;
  }

  function startSheenLoop() {
    if (sheenLoop) return;
    if (!SHEEN_ENABLED) return;
    if (!materialIsAnimated(material)) return;
    if (prefersReducedMotion()) return;
    if (!state || !state.sheenCtx) return;
    sheenLoop = { handle: null, startTs: 0, lastPaintTs: 0 };
    var tick = function (ts) {
      if (!sheenLoop) return;
      if (!sheenLoop.startTs) sheenLoop.startTs = ts;
      sheenLoop.handle = requestAnimationFrame(tick);
      // Defer paint when the tab or the modal is hidden; the loop keeps
      // running so it picks up immediately when the user reopens. Browser
      // already throttles rAF heavily when document.hidden, so the cost
      // of an empty tick is negligible.
      if (document.hidden) return;
      if (!isCanvasOnscreen()) return;
      if (ts - sheenLoop.lastPaintTs < SHEEN_FRAME_MS) return;
      sheenLoop.lastPaintTs = ts;
      paintMaterialFrame(state.sheenCtx, ts - sheenLoop.startTs);
    };
    sheenLoop.handle = requestAnimationFrame(tick);
    debug('sheenLoop', { state: 'started', material: material });
  }

  function stopSheenLoop() {
    if (!sheenLoop) return;
    if (sheenLoop.handle != null) {
      try { cancelAnimationFrame(sheenLoop.handle); } catch (_) {}
    }
    sheenLoop = null;
    debug('sheenLoop', { state: 'stopped' });
  }

  // Material backing fill — dispatched on material. Returns a CanvasGradient
  // (or a solid color string for glitter) suitable for `ctx.fillStyle`.
  // Used by paintMaterialFrame()'s step 2 (the die-cut backing). For glitter
  // the backing is a soft off-white; the sparkle dots are drawn in the face
  // pass so they cover the whole die-cut (border + face) — the entire
  // sticker reads as one glittery material per the brief's animated-border
  // recommendation.
  function materialBackingFill(rx, ry, rw, rh, phaseMs) {
    switch (material) {
      case 'holographic': return holoGradient(rx, ry, rw, rh, phaseMs);
      case 'chrome':      return chromeVerticalGradient(ry, rh);
      // Glitter always returns the solid backing color — this fill casts
      // the drop shadow in paintMaterialFrame() step 2; the opaque substrate
      // PNG is overlaid afterward as a single cover-scaled drawImage (no
      // tile repeat → no brightness-block seams). The solid color is also
      // the visible backing while the PNG is loading or if it fails.
      case 'glitter':     return SHEEN.glitter.backingColor;
    }
    return '#ffffff';
  }

  // Scale `img` to "cover" the (w, h) region preserving aspect ratio,
  // centered — the larger of the two side-ratios wins, and any overflow
  // is naturally clipped by the caller's path clip. Single drawImage call
  // has no internal tile boundary, so no seams in any state. Used by
  // paintMaterialFrame() for the glitter substrate (replaces the prior
  // createPattern('repeat') backing that showed visible brightness-block
  // seams in the pre-trace flat square).
  function drawImageCover(img, x, y, w, h) {
    var iw = img.naturalWidth || img.width;
    var ih = img.naturalHeight || img.height;
    if (!iw || !ih || !w || !h) return;
    var scale = Math.max(w / iw, h / ih);
    var dw = iw * scale;
    var dh = ih * scale;
    var dx = x + (w - dw) / 2;
    var dy = y + (h - dh) / 2;
    ctx.drawImage(img, dx, dy, dw, dh);
  }

  function holoGradient(rx, ry, rw, rh, phaseMs) {
    var cfg = SHEEN.holographic;
    var p   = (phaseMs % cfg.cycleMs) / cfg.cycleMs;       // 0..1
    var grad = ctx.createLinearGradient(rx, ry, rx + rw, ry + rh);
    var n = HOLO_PALETTE.length;
    var stops = [];
    for (var i = 0; i < n; i++) {
      var at = ((i / (n - 1)) + p) % 1;
      stops.push([at, HOLO_PALETTE[i]]);
    }
    stops.sort(function (a, b) { return a[0] - b[0]; });
    for (var j = 0; j < stops.length; j++) grad.addColorStop(stops[j][0], stops[j][1]);
    return grad;
  }

  function chromeVerticalGradient(ry, rh) {
    var grad = ctx.createLinearGradient(0, ry, 0, ry + rh);
    // Dark band stops lifted from ~18% luminance to ~33–37% per owner Dev QA
    // feedback (2026-06-14) — original was reading as too heavy. Light stops
    // unchanged; the metallic contrast is preserved, just brighter overall.
    grad.addColorStop(0.00, '#f5f7fa');
    grad.addColorStop(0.22, '#5b6573');
    grad.addColorStop(0.50, '#e3e9f1');
    grad.addColorStop(0.78, '#525b68');
    grad.addColorStop(1.00, '#eff3f8');
    return grad;
  }

  // Face sheen — drawn on top of the customer's art (which sits on the
  // material backing). Source-over blends at moderate alphas so the
  // finish reads on BOTH light and dark art (the Brief 2 revision fix —
  // `overlay`/`screen` were identity on bright colors). The art pixels
  // are not modified — these are additive layers above them; SHEEN_ENABLED
  // = false skips both passes and the render reverts to vinyl byte-equal.
  function drawFaceSheen(facePath, rx, ry, rw, rh, phaseMs) {
    if (!facePath || !rw || !rh) return;
    switch (material) {
      case 'holographic': drawHolographicFace(facePath, rx, ry, rw, rh, phaseMs); break;
      case 'chrome':      drawChromeFace(facePath, rx, ry, rw, rh, phaseMs);      break;
      case 'glitter':     drawGlitterFace(facePath, rx, ry, rw, rh, phaseMs);     break;
    }
  }

  function drawHolographicFace(facePath, rx, ry, rw, rh, phaseMs) {
    var cfg = SHEEN.holographic;
    var grad = holoGradient(rx, ry, rw, rh, phaseMs);

    ctx.save();
    ctx.clip(facePath);
    // Pass 1: source-over rainbow tint — visible on both bright and dark art.
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = cfg.tintAlpha;
    ctx.fillStyle = grad;
    ctx.fillRect(rx, ry, rw, rh);
    // Pass 2: overlay accent — boosts mid-tone iridescence.
    ctx.globalCompositeOperation = 'overlay';
    ctx.globalAlpha = cfg.overlayAlpha;
    ctx.fillStyle = grad;
    ctx.fillRect(rx, ry, rw, rh);
    ctx.restore();
  }

  function drawChromeFace(facePath, rx, ry, rw, rh, phaseMs) {
    var cfg = SHEEN.chrome;
    var p   = (phaseMs % cfg.cycleMs) / cfg.cycleMs;

    ctx.save();
    ctx.clip(facePath);

    // Pass 1: source-over vertical metal banding — visible on bright art.
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = cfg.bandAlpha;
    ctx.fillStyle = chromeVerticalGradient(ry, rh);
    ctx.fillRect(rx, ry, rw, rh);

    // Pass 2: animated diagonal specular streak — source-over so it reads
    // on dark art too. Soft edges via a gradient with transparent ends.
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = cfg.specularAlpha;
    var sw = rw * 0.24;
    var travel = rw + sw * 2;
    var cx = rx - sw + travel * p;
    var sgrad = ctx.createLinearGradient(cx - sw, ry, cx + sw, ry + rh);
    sgrad.addColorStop(0.0, 'rgba(255,255,255,0)');
    sgrad.addColorStop(0.5, 'rgba(255,255,255,1)');
    sgrad.addColorStop(1.0, 'rgba(255,255,255,0)');
    ctx.fillStyle = sgrad;
    ctx.fillRect(rx, ry, rw, rh);

    ctx.restore();
  }

  function drawGlitterFace(facePath, rx, ry, rw, rh, phaseMs) {
    var cfg = SHEEN.glitter;

    // Owner-supplied tileable PNG path. Mostly-transparent texture composited
    // source-over so the transparent pixels paint nothing and the sparkle
    // pixels paint at their native color. Falls back to procedural dots
    // while the image is loading or if it failed to load — glitter PDPs
    // never render blank.
    if (glitterPatternState === 'ready' && glitterPatternImg) {
      var pattern = ctx.createPattern(glitterPatternImg, 'repeat');
      if (pattern) {
        ctx.save();
        ctx.clip(facePath);
        ctx.globalCompositeOperation = 'source-over';
        ctx.globalAlpha = cfg.patternAlpha;
        // Subtle drift — translate the pattern in canvas-coord space so the
        // sparkles slowly slide. Diagonal drift, looped to the pattern tile.
        var driftP = (phaseMs % cfg.patternDriftMs) / cfg.patternDriftMs;
        var tile   = (glitterPatternImg.naturalWidth || 1024) * cfg.patternScale;
        if (typeof DOMMatrix !== 'undefined' && typeof pattern.setTransform === 'function') {
          var m = new DOMMatrix()
            .translateSelf(driftP * tile, driftP * tile)
            .scaleSelf(cfg.patternScale, cfg.patternScale);
          try { pattern.setTransform(m); } catch (_) { /* old WebKit — skip drift */ }
        }
        ctx.fillStyle = pattern;
        ctx.fillRect(rx, ry, rw, rh);
        ctx.restore();
        return;
      }
    }

    // Procedural fallback — also runs at script-load time before the pattern
    // PNG arrives, and forever if the PNG asset is missing / fails.
    ensureGlitterField(state.glitterSeed || 1, { x: rx, y: ry, w: rw, h: rh });
    var dots = state.glitterField.dots;
    var omega = (2 * Math.PI) / cfg.twinkleMs;
    var t = phaseMs * omega;

    ctx.save();
    ctx.clip(facePath);
    // source-over (not screen) so white sparkles render on bright art too.
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = '#ffffff';
    for (var i = 0; i < dots.length; i++) {
      var d = dots[i];
      var twinkle = 0.40 + 0.60 * (0.5 + 0.5 * Math.sin(t + d.phaseOff));
      ctx.globalAlpha = d.base * twinkle;
      ctx.beginPath();
      ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  // ────────────────────────────────────────────────────────────
  // Public API
  // ────────────────────────────────────────────────────────────

  function init(opts) {
    pane     = opts.pane;
    controls = opts.controls || null;
    if (!pane) return null;
    canvas             = pane.querySelector('[data-stk-preview-canvas]');
    emptyEl            = pane.querySelector('[data-stk-preview-empty]');
    noteEl             = pane.querySelector('[data-stk-preview-note]');
    statusEl           = pane.querySelector('[data-stk-preview-status]');
    stepperEl          = pane.querySelector('[data-stk-preview-stepper]');
    stepperStepsEl     = pane.querySelector('[data-stk-preview-stepper-steps]');
    stepperTrackfillEl = pane.querySelector('[data-stk-preview-stepper-trackfill]');
    stepperLabelEl     = pane.querySelector('[data-stk-preview-stepper-label]');
    stepperSublabelEl  = pane.querySelector('[data-stk-preview-stepper-sublabel]');
    if (!canvas) return null;
    ctx = canvas.getContext('2d');
    canvas.width  = CANVAS_SIZE;
    canvas.height = CANVAS_SIZE;
    onStateChange = opts.onStateChange || null;
    material = normalizeMaterial(opts.material);
    glitterPatternUrl = opts.glitterPatternUrl || null;
    glitterBackgroundUrl = opts.glitterBackgroundUrl || null;
    state = freshState();
    setupControls();
    setupReducedMotionListener();
    if (material === 'glitter') {
      if (glitterBackgroundUrl) loadGlitterBackground();
      if (glitterPatternUrl)    loadGlitterPattern();
    }
    showEmpty();
    // init() ran — separate from the script-load BUILD stamp at IIFE eval.
    debug('init()', {
      hasCanvas: !!canvas, hasControls: !!controls,
      material: material, materialRaw: opts.material || null,
      glitterBackgroundUrl: glitterBackgroundUrl,
      glitterPatternUrl: glitterPatternUrl,
    });
    window.addEventListener('resize', function () {
      if (state && state.img) render();
    });
    return {
      setFile: setFile,
      clear:   clear,
      getMeta: getMeta,
    };
  }

  function setFile(file) {
    cancelPending();
    stopSheenLoop();
    resetStepper(/*revealCanvas*/ false);
    state = freshState();
    state.file = file;
    state.decision = 'pending';
    state.glitterSeed = hashSeed(file);

    debug('setFile', {
      name: file && file.name,
      type: file && file.type,
      sizeKB: file && file.size ? Math.round(file.size / 1024) : 0,
    });

    if (file.size > CLOUDINARY.maxBytes) {
      showUnrenderable('Image is too large to preview (max 10 MB). We’ll still print from your upload.');
      return;
    }

    if (isSvgFile(file)) {
      // SVG — rasterized client-side, then routed through the same alpha-
      // decision the vector path uses. Cloudinary never sees the SVG bytes
      // (unsigned SVG upload is blocked + stored SVG carries an XSS surface);
      // only the rasterized PNG is uploaded, and only for the opaque case.
      svgClientRasterPath(file);
      return;
    }

    if (isVectorFile(file)) {
      // PDF / AI / EPS — rasterize via Cloudinary (no bg-removal), then feed
      // the resulting PNG into the same flat-first + silhouette-trace path
      // raster uploads use.
      vectorCloudinaryPath(file);
      return;
    }

    var reader = new FileReader();
    reader.onload = function (e) {
      var url = e.target.result;
      var isRaster = /^data:image\/(png|jpe?g|webp)/i.test(url);
      debug('reader', { isRaster: isRaster, head: String(url).slice(0, 32) });
      if (!isRaster) {
        showUnrenderable('Preview not available for that file type — we’ll set up your proof manually.');
        return;
      }
      var img = new Image();
      img.onload = function () {
        debug('img.onload', { w: img.naturalWidth, h: img.naturalHeight });
        if (state.file !== file) return;
        state.img = img;
        renderFlatFirst();               // <100 ms honest first paint
        var hasAlpha;
        try {
          hasAlpha = imageHasAlpha(img);
        } catch (e) {
          debug('imageHasAlpha threw', String(e && e.message ? e.message : e));
          hasAlpha = false;
        }
        debug('imageHasAlpha', hasAlpha);
        debug('route', hasAlpha ? 'transparent/local' : 'cloudinary');
        if (hasAlpha) {
          traceFromImage(buildSilhouette(img), /*silhouette*/ true);
        } else {
          cloudinaryPath(file);
        }
      };
      img.onerror = function () {
        debug('img.onerror');
        showUnrenderable('Could not load that image — we’ll set up your proof manually.');
      };
      img.src = url;
    };
    reader.readAsDataURL(file);
  }

  function clear() {
    cancelPending();
    stopSheenLoop();
    resetStepper(/*revealCanvas*/ false);
    state = freshState();
    showEmpty();
    notify();
  }

  function getMeta() {
    return {
      shape:           state.controls.shape,
      bleed:           state.controls.bleed,
      round:           state.controls.round,
      fit:             state.controls.fit,
      bg:              state.controls.bg,
      conf:            state.conf,
      decision:        state.decision,
      used_cloudinary: state.used_cloudinary,
      public_id:       state.public_id,
      secure_url:      state.secure_url,
      material:        material,
    };
  }

  function notify() {
    debug('decision', {
      decision:        state.decision,
      shape:           state.controls.shape,
      bleed:           state.controls.bleed,
      used_cloudinary: state.used_cloudinary,
      conf:            state.conf != null ? round3(state.conf) : null,
      why:             state.why || null,
      hasPaths:        !!state.paths,
    });
    if (onStateChange) onStateChange(getMeta());
  }

  function cancelPending() {
    if (pollHandle != null) { clearTimeout(pollHandle); pollHandle = null; }
    if (uploadAbort) { try { uploadAbort.abort(); } catch (_) {} uploadAbort = null; }
    if (traceAbort)  { try { traceAbort.abort();  } catch (_) {} traceAbort  = null; }
  }

  // ────────────────────────────────────────────────────────────
  // Display state helpers
  // ────────────────────────────────────────────────────────────

  function showEmpty() {
    resetStepper(/*revealCanvas*/ false);
    if (canvas)  canvas.style.display  = 'none';
    if (emptyEl) emptyEl.style.display = '';
    if (noteEl)  noteEl.style.display  = 'none';
    if (controls) controls.hidden      = true;
    setStatus('');
  }

  function showCanvas() {
    if (canvas)  canvas.style.display  = 'block';
    if (emptyEl) emptyEl.style.display = 'none';
    if (noteEl)  noteEl.style.display  = 'none';
    if (controls) controls.hidden      = false;
  }

  function showUnrenderable(msg) {
    state.decision = 'unrenderable';
    state.why = msg;
    // Tear down the stepper before showing the note — no half-built stepper
    // residue on terminal-error paths (brief 2026-06-18, acceptance §1).
    resetStepper(/*revealCanvas*/ false);
    if (canvas)  canvas.style.display  = 'none';
    if (emptyEl) emptyEl.style.display = 'none';
    if (noteEl) {
      noteEl.style.display = '';
      noteEl.textContent = msg;
    }
    if (controls) controls.hidden = true;
    setStatus('');
    notify();
  }

  function setStatus(text) {
    if (statusEl) statusEl.textContent = text || '';
  }

  // ────────────────────────────────────────────────────────────
  // Stage stepper — visible UI for async preview phases.
  //
  // Event-driven: every advance call (setPhase) reflects a real transition in
  // the pipeline (Cloudinary rasterize / bg removal / /api/trace round-trip).
  // Stages are added incrementally as they're needed so 2-stage paths
  // (raster + bg / vector transparent) never render a dead third dot. A slow
  // /api/trace keeps the scissors stage honestly spinning until the response
  // lands — there is no fallback timer.
  //
  // The canvas is hidden while the stepper is up; on setPhase('done') we hold
  // the success state for DONE_HOLD_MS then reveal the canvas (re-rendering
  // to pick up the now-correct CSS dimensions).
  // ────────────────────────────────────────────────────────────

  function setPhase(key) {
    if (!stepperEl) {
      // Stepper markup not in this section — fall back silently (older builds
      // of the preview pane don't have the stepper container).
      if (key && key !== 'done' && STAGE_META[key]) setStatus(STAGE_META[key].label);
      else if (key === 'done') setStatus(DONE_META.label);
      else if (!key)           setStatus('');
      return;
    }
    if (key === 'done') {
      showStepperDone();
      return;
    }
    if (!STAGE_META[key]) return;
    if (stepper.doneHideTimer != null) {
      clearTimeout(stepper.doneHideTimer);
      stepper.doneHideTimer = null;
    }
    ensureStepperVisible();
    ensureStageRendered(key);
    stepper.current = key;
    renderStepperState();
  }

  function ensureStepperVisible() {
    if (stepper.active) return;
    stepper.active = true;
    if (canvas)  canvas.style.display  = 'none';
    if (emptyEl) emptyEl.style.display = 'none';
    if (noteEl)  noteEl.style.display  = 'none';
    if (controls) controls.hidden = true;
    stepperEl.hidden = false;
    stepperEl.classList.remove('is-done');
    if (stepperLabelEl)    stepperLabelEl.textContent    = '';
    if (stepperSublabelEl) stepperSublabelEl.textContent = '';
    removeAllStepNodes();
    if (stepperTrackfillEl) stepperTrackfillEl.style.width = '0%';
  }

  function ensureStageRendered(key) {
    if (stepper.stages.indexOf(key) !== -1) return;
    var next = [];
    for (var i = 0; i < STAGE_ORDER.length; i++) {
      var s = STAGE_ORDER[i];
      if (s === key || stepper.stages.indexOf(s) !== -1) next.push(s);
    }
    stepper.stages = next;
    rebuildStepNodes();
  }

  function removeAllStepNodes() {
    if (!stepperStepsEl) return;
    var nodes = stepperStepsEl.querySelectorAll('.stk__preview-step');
    for (var i = 0; i < nodes.length; i++) nodes[i].parentNode.removeChild(nodes[i]);
  }

  function rebuildStepNodes() {
    if (!stepperStepsEl) return;
    removeAllStepNodes();
    for (var i = 0; i < stepper.stages.length; i++) {
      var key = stepper.stages[i];
      var meta = STAGE_META[key];
      var step = document.createElement('div');
      step.className = 'stk__preview-step';
      step.setAttribute('data-stk-preview-step', key);
      var ring = document.createElement('span');
      ring.className = 'stk__preview-step-ring';
      var icon = document.createElement('span');
      icon.className = 'stk__preview-step-icon';
      icon.innerHTML = meta.icon;
      ring.appendChild(icon);
      step.appendChild(ring);
      stepperStepsEl.appendChild(step);
    }
  }

  function renderStepperState() {
    if (!stepperStepsEl) return;
    var stages = stepper.stages;
    var N = stages.length;
    var idx = stages.indexOf(stepper.current);
    for (var i = 0; i < N; i++) {
      var key = stages[i];
      var node = stepperStepsEl.querySelector('[data-stk-preview-step="' + key + '"]');
      if (!node) continue;
      var ring = node.querySelector('.stk__preview-step-ring');
      var iconWrap = node.querySelector('.stk__preview-step-icon');
      node.classList.remove('is-active', 'is-done');
      var existingArc = ring && ring.querySelector('.stk__preview-step-arc');
      if (existingArc) existingArc.parentNode.removeChild(existingArc);
      if (i < idx) {
        node.classList.add('is-done');
        if (iconWrap) iconWrap.innerHTML = SVG_CHECK;
      } else if (i === idx) {
        node.classList.add('is-active');
        if (iconWrap) iconWrap.innerHTML = STAGE_META[key].icon;
        var arc = document.createElement('span');
        arc.className = 'stk__preview-step-arc';
        arc.setAttribute('aria-hidden', 'true');
        if (ring) ring.appendChild(arc);
      } else {
        if (iconWrap) iconWrap.innerHTML = STAGE_META[key].icon;
      }
    }
    if (stepperTrackfillEl) {
      // Width math mirrors the mockup's 11 / 50 / 89 / 100 pattern, generalized
      // across N stages so the fill terminates under the active ring's center.
      var pct;
      if (N <= 1) pct = idx >= 0 ? 50 : 0;
      else        pct = (idx / (N - 1)) * 78 + 11;
      stepperTrackfillEl.style.width = pct + '%';
    }
    var meta = STAGE_META[stepper.current];
    if (meta) {
      if (stepperLabelEl)    stepperLabelEl.textContent    = meta.label;
      if (stepperSublabelEl) stepperSublabelEl.textContent = meta.sub;
      setStatus(meta.label);
    }
  }

  function showStepperDone() {
    if (!stepperEl) return;
    if (!stepper.active) {
      // No stepper was ever shown for this upload (e.g. instant flat path with
      // no async stage). Skip the success beat and reveal canvas immediately.
      if (state && state.img) {
        showCanvas();
        try { render(); } catch (_) {}
      }
      return;
    }
    if (stepper.doneHideTimer != null) {
      clearTimeout(stepper.doneHideTimer);
      stepper.doneHideTimer = null;
    }
    stepper.current = 'done';
    var stages = stepper.stages;
    for (var i = 0; i < stages.length; i++) {
      var node = stepperStepsEl && stepperStepsEl.querySelector('[data-stk-preview-step="' + stages[i] + '"]');
      if (!node) continue;
      node.classList.remove('is-active');
      node.classList.add('is-done');
      var ring = node.querySelector('.stk__preview-step-ring');
      var existingArc = ring && ring.querySelector('.stk__preview-step-arc');
      if (existingArc) existingArc.parentNode.removeChild(existingArc);
      var iconWrap = node.querySelector('.stk__preview-step-icon');
      if (iconWrap) iconWrap.innerHTML = SVG_CHECK;
    }
    stepperEl.classList.add('is-done');
    if (stepperTrackfillEl) stepperTrackfillEl.style.width = '100%';
    if (stepperLabelEl)     stepperLabelEl.textContent     = DONE_META.label;
    if (stepperSublabelEl)  stepperSublabelEl.textContent  = DONE_META.sub;
    setStatus(DONE_META.label);
    stepper.doneHideTimer = setTimeout(function () {
      stepper.doneHideTimer = null;
      resetStepper(/*revealCanvas*/ true);
    }, DONE_HOLD_MS);
  }

  function resetStepper(revealCanvas) {
    if (stepper.doneHideTimer != null) {
      clearTimeout(stepper.doneHideTimer);
      stepper.doneHideTimer = null;
    }
    stepper.active  = false;
    stepper.stages  = [];
    stepper.current = null;
    if (stepperEl) {
      stepperEl.hidden = true;
      stepperEl.classList.remove('is-done');
    }
    removeAllStepNodes();
    if (stepperTrackfillEl) stepperTrackfillEl.style.width = '0%';
    if (stepperLabelEl)     stepperLabelEl.textContent     = '';
    if (stepperSublabelEl)  stepperSublabelEl.textContent  = '';
    if (revealCanvas && state && state.img) {
      showCanvas();
      // Canvas just became visible — repaint so applyCanvasDPR() can read
      // real CSS dims (getBoundingClientRect returns 0,0 while display:none).
      try { render(); } catch (_) {}
    }
  }

  // ────────────────────────────────────────────────────────────
  // Path A — Transparent uploads (local silhouette → /api/trace)
  // ────────────────────────────────────────────────────────────

  function traceFromImage(traceSrc, isSilhouette) {
    debug('traceFromImage', { isSilhouette: !!isSilhouette });
    setPhase('trace');
    state.matteImg = traceSrc;

    // Defer one frame so the flat-first paint shows before the gate runs.
    requestAnimationFrame(function () {
      debug('rAF fired');
      if (state.matteImg !== traceSrc) return;
      var gated = runGate(traceSrc);
      state.conf = gated.conf;
      state.why  = gated.why;

      if (!gated.pass) {
        // Gate said don't trace — fall back to honest flat. /api/trace not called.
        state.paths = null;
        state.decision = state.controls.shape === 'custom' ? 'flat' : 'geometric';
        render();
        setPhase('done');
        notify();
        return;
      }

      // Gate passed → call server. Build the payload from whichever source
      // we have. Cloudinary path can pass public_id (server fetches a width-
      // capped matte). Transparent path encodes the silhouette canvas to PNG.
      var payload;
      if (state.used_cloudinary && state.public_id) {
        payload = { cloudinaryId: state.public_id };
      } else if (isSilhouette && traceSrc.toDataURL) {
        payload = { silhouettePngB64: traceSrc.toDataURL('image/png').split(',')[1] };
      } else {
        // Defensive: encode whatever we have to PNG. The trace source must be
        // a canvas-like object (buildSilhouette returns a <canvas>; Cloudinary
        // matte is an <img> without toDataURL → drawn to an off-screen canvas).
        payload = { silhouettePngB64: encodeImageAsPng(traceSrc) };
      }

      // Payload size — multi-MB base64 bodies have rejected on mobile in the wild.
      debug('payload', {
        kind: payload.cloudinaryId ? 'cloudinaryId' : 'silhouettePngB64',
        b64KB: payload.silhouettePngB64 ? Math.round(payload.silhouettePngB64.length / 1024) : 0,
      });

      apiTrace(payload)
        .then(function (resp) {
          if (state.matteImg !== traceSrc) return; // user changed file mid-flight
          if (!resp || !resp.ok) {
            debug('apiTrace failed', resp);
            state.paths = null;
            state.decision = state.controls.shape === 'custom' ? 'flat' : 'geometric';
          } else {
            state.paths = resp;
            state.decision = state.controls.shape === 'custom' ? 'contour' : 'geometric';
            debug('apiTrace ok', {
              w: resp.w, h: resp.h,
              thinSegs:   resp.thin   && resp.thin.segments,
              normalSegs: resp.normal && resp.normal.segments,
              wideSegs:   resp.wide   && resp.wide.segments,
            });
          }
          render();
          setPhase('done');
          notify();
        })
        .catch(function (err) {
          if (state.matteImg !== traceSrc) return;
          // The most likely culprit on iOS: a failed/blocked cross-origin POST,
          // a CSP `connect-src` rejection, or an ITP/network error all surface
          // here. Route to debug() so it lands on the on-screen panel.
          debug('apiTrace ERROR', err && err.message ? err.message : String(err));
          state.paths = null;
          state.decision = state.controls.shape === 'custom' ? 'flat' : 'geometric';
          render();
          setPhase('done');
          notify();
        });
    });
  }

  function apiTrace(payload) {
    traceAbort = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    var opts = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    };
    if (traceAbort) opts.signal = traceAbort.signal;
    debug('apiTrace POST', {
      payloadKey: payload.cloudinaryId ? 'cloudinaryId' : 'silhouettePngB64',
      cloudinaryId: payload.cloudinaryId || null,
      pngB64Len: payload.silhouettePngB64 ? payload.silhouettePngB64.length : 0,
    });
    debug('apiTrace →', TRACE_ENDPOINT);
    return fetch(TRACE_ENDPOINT, opts).then(function (r) {
      var status = r.status;
      return r.json().then(function (j) {
        debug('apiTrace HTTP', { status: status, ok: r.ok, err: j && j.error });
        if (!r.ok) {
          throw new Error('trace HTTP ' + status + ' (' + (j && j.error) + ')');
        }
        return j;
      });
    });
  }

  function encodeImageAsPng(srcImg) {
    var capped = drawCapped(srcImg);
    debug('encodeImageAsPng', {
      capW: capped.canvas.width,
      capH: capped.canvas.height,
      scale: round3(capped.scale),
    });
    return capped.canvas.toDataURL('image/png').split(',')[1];
  }

  // ────────────────────────────────────────────────────────────
  // Path B — Opaque uploads (Cloudinary background removal)
  // ────────────────────────────────────────────────────────────

  function cloudinaryPath(file) {
    setPhase('bg');
    cloudinaryUpload(file)
      .then(function (info) {
        if (state.file !== file) return;
        state.public_id  = info.public_id;
        state.secure_url = info.secure_url;
        state.used_cloudinary = true;
        var matteUrl = CLOUDINARY.matteBase + '/' + CLOUDINARY.transform + '/' + encodeURIComponent(info.public_id) + '.png';
        return pollMatte(matteUrl, file);
      })
      .then(function (matteImg) {
        if (!matteImg) return;
        if (state.file !== file) return;
        traceFromImage(matteImg, /*silhouette*/ false);
      })
      .catch(function (err) {
        if (state.file !== file) return;
        debug('cloudinary ERROR', err && err.message ? err.message : String(err));
        state.decision = state.controls.shape === 'custom' ? 'flat' : 'geometric';
        render();
        setPhase('done');
        notify();
      });
  }

  // ────────────────────────────────────────────────────────────
  // Path C — Vector uploads (PDF / AI / EPS) — Round 17, 2026-06-18
  //
  // Cloudinary rasterizes server-side via Ghostscript. Delivered as PNG with
  // bg-removal OFF (see CLOUDINARY.vectorTransform). The rasterized PNG then
  // re-enters the existing flat-first + hasAlpha branch the raster path uses:
  //   alpha → silhouette → POST /api/trace with silhouettePngB64
  //   opaque → flat render (no bg-removal re-fetch)
  // No bridge-repo (/api/trace) change is needed because the silhouette path
  // already sends a PNG payload, not a cloudinaryId. state.used_cloudinary is
  // intentionally left false so traceFromImage does not route to the
  // bg-removal server-fetch branch; public_id + secure_url ARE set so the
  // cart line item still ships a Cloudinary URL.
  // ────────────────────────────────────────────────────────────

  function isVectorFile(file) {
    var mime = (file && file.type ? file.type : '').toLowerCase();
    if (mime === 'application/pdf') return true;
    if (mime === 'application/postscript') return true;       // .ai, .eps (varies by browser)
    if (mime === 'application/illustrator') return true;
    // Some browsers (Safari, Windows) don't set MIME for .ai/.eps. Fall back
    // to extension on the filename the customer uploaded.
    var name = (file && file.name ? file.name : '').toLowerCase();
    return /\.(pdf|ai|eps)$/.test(name);
  }

  function vectorCloudinaryPath(file) {
    setPhase('prepare');
    cloudinaryUpload(file)
      .then(function (info) {
        if (state.file !== file) return null;
        state.public_id  = info.public_id;
        state.secure_url = info.secure_url;
        // NOTE: do NOT set state.used_cloudinary = true here. That flag tells
        // traceFromImage to send a cloudinaryId payload — which the server
        // resolves with its bg-removal transform. We rasterize without
        // bg-removal on purpose; the silhouette path sends a PNG instead.
        var vectorUrl = CLOUDINARY.matteBase + '/' + CLOUDINARY.vectorTransform + '/' + encodeURIComponent(info.public_id) + '.png';
        debug('vectorCloudinaryPath', { public_id: info.public_id });
        return pollMatte(vectorUrl, file);
      })
      .then(function (vectorImg) {
        if (!vectorImg) return;
        if (state.file !== file) return;
        state.img = vectorImg;
        renderFlatFirst();
        var hasAlpha;
        try {
          hasAlpha = imageHasAlpha(vectorImg);
        } catch (e) {
          debug('vector imageHasAlpha threw', String(e && e.message ? e.message : e));
          hasAlpha = false;
        }
        debug('vector route', hasAlpha ? 'silhouette' : 'opaque→knockout');
        if (hasAlpha) {
          traceFromImage(buildSilhouette(vectorImg), /*silhouette*/ true);
        } else {
          vectorKnockoutAndTrace(file);
        }
      })
      .catch(function (err) {
        if (state.file !== file) return;
        debug('vectorCloudinaryPath ERROR', err && err.message ? err.message : String(err));
        showUnrenderable('Couldn’t render that file for preview — we’ll set up your proof manually.');
      });
  }

  // Opaque-vector branch (Item 1b, 2026-06-18). Reached when the no-bg-removal
  // rasterization has zero alpha — the customer's PDF/AI/EPS shipped an opaque
  // background, so the contour can't be traced from alpha alone. Poll the same
  // public_id through a second derived URL that DOES apply bg-removal, then
  // trace that matte. Keep state.img pointed at the original rasterized vector
  // so the customer's pixels still draw inside the cut shape — only the
  // CUTLINE is derived from the bg-removed matte (A8 fidelity rule).
  //
  // Failure modes that fall through to flat render (never "preview not available"):
  //   - pollMatte rejects (Cloudinary error, network)
  //   - matte returns still-opaque (bg-removal failed silently)
  //   - matte returns all-transparent (subject not detected) — handled by
  //     the existing runGate "no foreground" path inside traceFromImage
  function vectorKnockoutAndTrace(file) {
    setPhase('bg');
    var knockoutUrl = CLOUDINARY.matteBase + '/' + CLOUDINARY.vectorKnockoutTransform + '/' + encodeURIComponent(state.public_id) + '.png';
    debug('vectorKnockoutAndTrace', { public_id: state.public_id });
    pollMatte(knockoutUrl, file)
      .then(function (matteImg) {
        if (!matteImg) return;
        if (state.file !== file) return;
        var matteHasAlpha;
        try {
          matteHasAlpha = imageHasAlpha(matteImg);
        } catch (e) {
          debug('knockout imageHasAlpha threw', String(e && e.message ? e.message : e));
          matteHasAlpha = false;
        }
        if (matteHasAlpha) {
          traceFromImage(buildSilhouette(matteImg), /*silhouette*/ true);
        } else {
          debug('knockout returned no alpha → flat fallback');
          vectorFlatFallback();
        }
      })
      .catch(function (err) {
        if (state.file !== file) return;
        debug('vectorKnockoutAndTrace ERROR', err && err.message ? err.message : String(err));
        vectorFlatFallback();
      });
  }

  function vectorFlatFallback() {
    state.decision = state.controls.shape === 'custom' ? 'flat' : 'geometric';
    render();
    setPhase('done');
    notify();
  }

  // ────────────────────────────────────────────────────────────
  // Path D — SVG uploads — Item 2, 2026-06-18
  //
  // Cloudinary blocks unsigned SVG uploads and stored SVGs carry an XSS
  // surface, so the SVG bytes never leave the client. We rasterize the SVG
  // to a PNG on a capped canvas, then route through the SAME alpha-decision
  // the vector path uses:
  //   transparent rasterization → local silhouette trace (no Cloudinary call)
  //   opaque rasterization      → upload the rasterized PNG to Cloudinary
  //                               and run vectorKnockoutAndTrace (Item 1b)
  // So opaque (Canva-style) SVGs get die-cut quality identical to opaque
  // PDFs, without ever exposing the SVG to the upload pipeline.
  //
  // Safari canvas-taint: drawing an SVG <img> into a canvas can taint that
  // canvas if the SVG contains <foreignObject> or external references.
  // getImageData / toBlob then throw SecurityError. Both call sites are
  // wrapped — taint falls through to vectorFlatFallback, never to
  // "preview not available."
  // ────────────────────────────────────────────────────────────

  function isSvgFile(file) {
    var mime = (file && file.type ? file.type : '').toLowerCase();
    if (mime === 'image/svg+xml') return true;
    var name = (file && file.name ? file.name : '').toLowerCase();
    return /\.svg$/.test(name);
  }

  function svgClientRasterPath(file) {
    var objectUrl;
    try {
      objectUrl = URL.createObjectURL(file);
    } catch (e) {
      debug('svg createObjectURL threw', e && e.message);
      showUnrenderable('Couldn’t read that SVG — we’ll set up your proof manually.');
      return;
    }
    var svgImg = new Image();
    svgImg.onload = function () {
      if (state.file !== file) { try { URL.revokeObjectURL(objectUrl); } catch (_) {} return; }
      var nw = svgImg.naturalWidth  || 0;
      var nh = svgImg.naturalHeight || 0;
      var aspect = (nw && nh) ? (nw / nh) : 1;
      var targetW, targetH;
      if (aspect >= 1) {
        targetW = WORKING_MAX_CLIENT;
        targetH = Math.max(1, Math.round(WORKING_MAX_CLIENT / aspect));
      } else {
        targetH = WORKING_MAX_CLIENT;
        targetW = Math.max(1, Math.round(WORKING_MAX_CLIENT * aspect));
      }
      var c = document.createElement('canvas');
      c.width = targetW; c.height = targetH;
      try {
        c.getContext('2d').drawImage(svgImg, 0, 0, targetW, targetH);
      } catch (e) {
        debug('svg drawImage threw', e && e.message);
        try { URL.revokeObjectURL(objectUrl); } catch (_) {}
        vectorFlatFallback();
        return;
      }
      try { URL.revokeObjectURL(objectUrl); } catch (_) {}
      Object.defineProperty(c, 'naturalWidth',  { value: targetW, configurable: true });
      Object.defineProperty(c, 'naturalHeight', { value: targetH, configurable: true });
      state.img = c;
      renderFlatFirst();
      var hasAlpha;
      try {
        hasAlpha = imageHasAlpha(c);
      } catch (e) {
        debug('svg imageHasAlpha threw', String(e && e.message ? e.message : e));
        hasAlpha = false;
      }
      debug('svg route', { nw: nw, nh: nh, targetW: targetW, targetH: targetH, route: hasAlpha ? 'silhouette' : 'opaque→cloudinary knockout' });
      if (hasAlpha) {
        traceFromImage(buildSilhouette(c), /*silhouette*/ true);
        return;
      }
      setPhase('prepare');
      try {
        c.toBlob(function (blob) {
          if (state.file !== file) return;
          if (!blob) {
            debug('svg toBlob returned null (likely taint)');
            vectorFlatFallback();
            return;
          }
          var baseName = (file && file.name ? file.name : 'sticker.svg').replace(/\.[^.]+$/, '');
          var pngFile;
          try {
            pngFile = new File([blob], baseName + '.png', { type: 'image/png' });
          } catch (e) {
            pngFile = blob;
          }
          cloudinaryUpload(pngFile)
            .then(function (info) {
              if (state.file !== file) return null;
              state.public_id  = info.public_id;
              state.secure_url = info.secure_url;
              debug('svgClientRasterPath uploaded', { public_id: info.public_id });
              vectorKnockoutAndTrace(file);
            })
            .catch(function (err) {
              if (state.file !== file) return;
              debug('svg cloudinary upload ERROR', err && err.message ? err.message : String(err));
              vectorFlatFallback();
            });
        }, 'image/png');
      } catch (e) {
        debug('svg toBlob threw', e && e.message);
        vectorFlatFallback();
        return;
      }
    };
    svgImg.onerror = function () {
      try { URL.revokeObjectURL(objectUrl); } catch (_) {}
      debug('svg img.onerror');
      showUnrenderable('Couldn’t load that SVG — we’ll set up your proof manually.');
    };
    svgImg.src = objectUrl;
  }

  function cloudinaryUpload(file, folder) {
    var fd = new FormData();
    fd.append('file', file);
    fd.append('upload_preset', CLOUDINARY.preset);
    if (folder) fd.append('folder', folder);
    uploadAbort = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    var opts = { method: 'POST', body: fd };
    if (uploadAbort) opts.signal = uploadAbort.signal;
    return fetch(CLOUDINARY.uploadUrl, opts).then(function (res) {
      if (!res.ok) throw new Error('upload HTTP ' + res.status);
      return res.json();
    }).then(function (j) {
      if (!j || !j.public_id) throw new Error('no public_id');
      return { public_id: j.public_id, secure_url: j.secure_url };
    });
  }

  /* ── SVG normalisation ────────────────────────────────────────────────
     Cloudinary sniffs an SVG's format from its FIRST BYTES. A file that does
     not begin with an XML declaration sniffs as `raw`, and the unsigned preset
     rejects it: 400 "Raw file format svg not allowed" — measured 2026-08-31 on
     auto/upload, image/upload and raw/upload alike. Illustrator "Export As" and
     Figma both write SVGs without the declaration. Order #2869 lost its artwork
     this way. A BOM is tolerated but leading whitespace is NOT, so the
     declaration has to end up at byte 0.
     Fails open: any unexpected file, or any read error, uploads unchanged. */
  var TD_XML_DECL = '<?xml version="1.0" encoding="UTF-8"?>\n';

  function normalizeSvg(file) {
    var name = file && file.name ? String(file.name).toLowerCase() : '';
    var looksSvg = !!file && (file.type === 'image/svg+xml' || /\.svg$/.test(name));
    if (!looksSvg || !file.slice || !file.slice(0, 1).arrayBuffer) return Promise.resolve(file);

    return file.slice(0, 1024).arrayBuffer().then(function (buf) {
      var b = new Uint8Array(buf), i = 0;
      if (b[0] === 0xEF && b[1] === 0xBB && b[2] === 0xBF) i = 3;                 // BOM
      while (i < b.length && (b[i] === 0x20 || b[i] === 0x09 || b[i] === 0x0A || b[i] === 0x0D)) i++;
      var head = new TextDecoder().decode(b.subarray(i));

      // Already declared and already at byte 0 — leave the bytes alone.
      if (/^<\?xml[\s?]/.test(head)) return i === 0 ? file : new File([file.slice(i)], file.name, { type: 'image/svg+xml' });
      // Named .svg but not SVG markup — never rewrite it, that would corrupt it.
      if (!/<svg[\s>]/i.test(head)) return file;
      return new File([TD_XML_DECL, file.slice(i)], file.name, { type: 'image/svg+xml' });
    })['catch'](function () { return file; });
  }

  // Public uploader — both sticker builders call this at order-submit time to
  // capture the customer's original file in Cloudinary BEFORE signing the
  // payload. Uses a fresh AbortController per call (separate from any active
  // preview upload), so it cannot be cancelled by mid-flight preview state.
  // NOTE: the PREVIEW path (cloudinaryUpload, above) is deliberately NOT
  // normalised — SVGs reach it already rasterised to PNG, so it never sees SVG
  // bytes and normalising there would be dead code on a hot path.
  function uploadArtwork(file, opts) {
    var folder = (opts && opts.folder) || null;
    return normalizeSvg(file).then(function (f) {
      var fd = new FormData();
      fd.append('file', f);
      fd.append('upload_preset', CLOUDINARY.preset);
      if (folder) fd.append('folder', folder);
      return fetch(CLOUDINARY.uploadUrl, { method: 'POST', body: fd }).then(function (res) {
        if (!res.ok) throw new Error('upload HTTP ' + res.status);
        return res.json();
      }).then(function (j) {
        if (!j || !j.public_id) throw new Error('no public_id');
        return { public_id: j.public_id, secure_url: j.secure_url };
      });
    });
  }

  // Poll the EXACT delivery URL we'll consume — first hit on async bg-removal
  // is per-derived-size. crossOrigin must be set BEFORE src or getImageData
  // throws SecurityError on the gate.
  function pollMatte(url, file) {
    return new Promise(function (resolve, reject) {
      var schedule = CLOUDINARY.pollMs.slice();
      function attempt() {
        if (state.file !== file) { resolve(null); return; }
        var img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = function () {
          if (state.file === file) resolve(img);
          else resolve(null);
        };
        img.onerror = function () {
          if (schedule.length === 0) {
            reject(new Error('matte never ready'));
          } else {
            pollHandle = setTimeout(attempt, schedule.shift());
          }
        };
        img.src = url + '?t=' + Date.now();
      }
      attempt();
    });
  }

  // ────────────────────────────────────────────────────────────
  // Confidence gate — runs client-side BEFORE calling /api/trace.
  //
  // Pipeline: extractAlpha (full-res, perim-chroma + R5 fringe trim) →
  // alphaBboxCrop → downscaleMask (≤512 for cheap raster math) → assessMask.
  // ────────────────────────────────────────────────────────────

  function runGate(matteImg) {
    var fullDim = Math.max(matteImg.naturalWidth || matteImg.width, matteImg.naturalHeight || matteImg.height);
    var matteTrimFull = 0;
    if (state.used_cloudinary && GATE.matteTrimPx > 0) {
      var traceScale = Math.max(1, fullDim / GATE.gateMaxDim);
      matteTrimFull = Math.max(1, Math.round(GATE.matteTrimPx * traceScale));
    }
    var full = extractAlpha(matteImg, { trimPx: matteTrimFull });
    if (!full) {
      debug('extractAlpha → null (tainted canvas)');
      return { pass: false, conf: 0, why: 'no alpha (tainted canvas)' };
    }
    debug('matte', {
      W: full.W, H: full.H,
      matteTrimFull: matteTrimFull,
      midRatio:       round3(full.midRatio),
      perimChromaStd: round3(full.perimChromaStd),
    });

    var cropped = alphaBboxCrop(full.mask, full.W, full.H);
    if (!cropped) {
      debug('alphaBboxCrop → null (no foreground)');
      return { pass: false, conf: 0, why: 'no foreground' };
    }
    var scaled = downscaleMask(cropped.mask, cropped.W, cropped.H, GATE.gateMaxDim);

    var assessed = assessMask(scaled.mask, scaled.W, scaled.H,
      full.midRatio, full.perimChromaStd);
    debug('gate', {
      conf: round3(assessed.conf),
      threshold: GATE.confThreshold,
      components: assessed.components,
      why: assessed.why,
    });
    return {
      pass: assessed.conf >= GATE.confThreshold,
      conf: assessed.conf,
      why:  assessed.why,
    };
  }

  // Round-16 debug — auto-enable when ?stkdebug=1 is in the URL, OR when the
  // global TDStickerPreviewDebug is set by hand from the console. iOS Safari
  // ate the trace pipeline silently and Web Inspector needs a Mac, so the
  // breadcrumbs have to land on the phone's own screen.
  try {
    if (/[?&]stkdebug=1\b/.test(window.location.search)) {
      window.TDStickerPreviewDebug = true;
    }
  } catch (_) {}

  // The panel is created eagerly (now, at script-load time) instead of lazily
  // on first debug() call. That way the panel appears the moment the new JS
  // lands on the device — independent of init(), independent of any upload.
  // If you don't see the panel and the URL has ?stkdebug=1, the new JS isn't
  // loading (CDN cache, wrong theme, wrong page).
  var dbgPanel = null;
  function ensureDbgPanel() {
    if (!window.TDStickerPreviewDebug) return;
    if (dbgPanel) return;
    try {
      dbgPanel = document.createElement('div');
      dbgPanel.id = 'td-stk-debug';
      dbgPanel.style.cssText = 'position:fixed;top:0;left:0;right:0;z-index:2147483647;max-height:40vh;overflow:auto;font:11px/1.35 monospace;background:rgba(0,0,0,.9);color:#0f0;padding:8px;white-space:pre-wrap;word-break:break-all;pointer-events:auto;border-bottom:2px solid #0f0;';
      if (document.body) {
        document.body.appendChild(dbgPanel);
      } else {
        // Body not parsed yet — append on DOMContentLoaded.
        document.addEventListener('DOMContentLoaded', function () {
          try { document.body.appendChild(dbgPanel); } catch (_) {}
        });
      }
    } catch (_) { dbgPanel = null; }
  }
  function dbgPanelWrite(line) {
    if (!window.TDStickerPreviewDebug) return;
    ensureDbgPanel();
    if (!dbgPanel) return;
    try {
      var p = document.createElement('div');
      p.textContent = line;
      // If the panel isn't in the DOM yet (body wasn't ready), the line still
      // lives on the detached panel — the deferred appendChild will surface it.
      dbgPanel.appendChild(p);
      while (dbgPanel.childNodes.length > 40) dbgPanel.removeChild(dbgPanel.firstChild);
      dbgPanel.scrollTop = dbgPanel.scrollHeight;
    } catch (_) {}
  }
  // Stamp the panel immediately on script eval — proves the new JS reached the
  // device before init() ever runs.
  ensureDbgPanel();
  if (window.TDStickerPreviewDebug) {
    dbgPanelWrite('[td-sticker-preview] BUILD r16 (script loaded) ' + JSON.stringify({
      ua: (navigator && navigator.userAgent) || '',
      dpr: window.devicePixelRatio || 1,
      url: (window.location && window.location.href) || '',
    }));
  }

  function debug() {
    if (!window.TDStickerPreviewDebug) return;
    var args = ['[td-sticker-preview]'].concat(Array.prototype.slice.call(arguments));
    try { console.log.apply(console, args); } catch (_) {}
    // Build a single string for the panel. JSON.stringify objects; primitives as-is.
    try {
      var parts = [];
      for (var i = 0; i < arguments.length; i++) {
        var a = arguments[i];
        if (a == null) { parts.push(String(a)); }
        else if (typeof a === 'string' || typeof a === 'number' || typeof a === 'boolean') {
          parts.push(String(a));
        } else {
          try { parts.push(JSON.stringify(a)); }
          catch (_) { parts.push(String(a)); }
        }
      }
      dbgPanelWrite(parts.join(' '));
    } catch (_) {}
  }

  function round3(n) { return (n == null) ? n : Math.round(n * 1000) / 1000; }

  // ── Alpha extraction (sets crossOrigin pre-load on Cloudinary images) ──

  function extractAlpha(img, options) {
    options = options || {};
    var trimPxFull = (options.trimPx | 0);
    var srcW = img.naturalWidth || img.width;
    var srcH = img.naturalHeight || img.height;
    if (!srcW || !srcH) {
      debug('extractAlpha → null (zero dims)');
      return null;
    }
    var capped = drawCapped(img);
    var off = capped.canvas;
    var W = off.width;
    var H = off.height;
    // trimPx came in as full-res px (caller doesn't know about the cap).
    // Scale it to the capped dims so the alpha-fringe trim hits the same
    // physical band on the image. Always ≥1 once trim is requested.
    var trimPx = trimPxFull > 0 ? Math.max(1, Math.round(trimPxFull * capped.scale)) : 0;
    var c = off.getContext('2d');
    try {
      var d = c.getImageData(0, 0, W, H).data;
      var mask = new Uint8Array(W * H);
      var mid = 0, seen = 0;
      for (var i = 0, j = 0; i < mask.length; i++, j += 4) {
        var a = d[j + 3];
        mask[i] = a >= 128 ? 1 : 0;
        if (a > 8) { seen++; if (a > 40 && a < 215) mid++; }
      }
      var midRatio = seen ? mid / seen : 0;

      // R5 — alpha-fringe trim (Cloudinary path only; trimPx is 0 for transparent uploads).
      if (trimPx > 0) {
        mask = erodeMask(mask, W, H, trimPx);
      }

      // R4 — perimeter-band chroma std-dev.
      var perimChromaStd = 0;
      var inner = erodeMask(mask, W, H, GATE.perimBandPx);
      var sumR = 0, sumG = 0, sumB = 0;
      var sumR2 = 0, sumG2 = 0, sumB2 = 0;
      var bandN = 0;
      for (var k = 0, kj = 0; k < mask.length; k++, kj += 4) {
        if (mask[k] && !inner[k]) {
          var pr = d[kj], pg = d[kj + 1], pb = d[kj + 2];
          sumR += pr; sumG += pg; sumB += pb;
          sumR2 += pr * pr; sumG2 += pg * pg; sumB2 += pb * pb;
          bandN++;
        }
      }
      if (bandN >= 4) {
        var meanR = sumR / bandN, meanG = sumG / bandN, meanB = sumB / bandN;
        var varR = Math.max(0, sumR2 / bandN - meanR * meanR);
        var varG = Math.max(0, sumG2 / bandN - meanG * meanG);
        var varB = Math.max(0, sumB2 / bandN - meanB * meanB);
        perimChromaStd = Math.sqrt((varR + varG + varB) / 3) / 255;
      }

      debug('extractAlpha ok', {
        srcW: srcW, srcH: srcH,
        capW: W, capH: H,
        scale: round3(capped.scale),
        trimPxFull: trimPxFull, trimPx: trimPx,
        midRatio: round3(midRatio),
        perimChromaStd: round3(perimChromaStd),
      });
      return {
        mask: mask, W: W, H: H,
        midRatio:       midRatio,
        perimChromaStd: perimChromaStd,
      };
    } catch (e) {
      debug('extractAlpha → null (getImageData threw)', {
        srcW: srcW, srcH: srcH, capW: W, capH: H, msg: e && e.message,
      });
      return null;
    }
  }

  function alphaBboxCrop(mask, W, H) {
    var x0 = W, y0 = H, x1 = -1, y1 = -1;
    for (var y = 0; y < H; y++) {
      var row = y * W;
      for (var x = 0; x < W; x++) {
        if (mask[row + x]) {
          if (x < x0) x0 = x;
          if (x > x1) x1 = x;
          if (y < y0) y0 = y;
          if (y > y1) y1 = y;
        }
      }
    }
    if (x1 < 0) return null;
    var pad = 2;
    x0 = Math.max(0, x0 - pad); y0 = Math.max(0, y0 - pad);
    x1 = Math.min(W - 1, x1 + pad); y1 = Math.min(H - 1, y1 + pad);
    var cW = x1 - x0 + 1, cH = y1 - y0 + 1;
    var out = new Uint8Array(cW * cH);
    for (var yy = 0; yy < cH; yy++) {
      for (var xx = 0; xx < cW; xx++) {
        out[yy * cW + xx] = mask[(y0 + yy) * W + (x0 + xx)];
      }
    }
    return { mask: out, W: cW, H: cH, x0: x0, y0: y0 };
  }

  function downscaleMask(mask, W, H, maxDim) {
    var m = Math.max(W, H);
    if (m <= maxDim) return { mask: mask, W: W, H: H };
    var s = maxDim / m;
    var nW = Math.max(1, Math.round(W * s));
    var nH = Math.max(1, Math.round(H * s));
    var out = new Uint8Array(nW * nH);
    for (var y = 0; y < nH; y++) {
      var sy = Math.min(H - 1, Math.floor(y / s));
      for (var x = 0; x < nW; x++) {
        var sx = Math.min(W - 1, Math.floor(x / s));
        out[y * nW + x] = mask[sy * W + sx];
      }
    }
    return { mask: out, W: nW, H: nH };
  }

  // ── Confidence gate (mirrors probe.py assess_matte + R3/R4 signals) ──

  function assessMask(mask, W, H, midRatio, perimChromaStd) {
    var total = W * H, fg = 0;
    for (var i = 0; i < mask.length; i++) if (mask[i]) fg++;
    var cov = fg / total;

    var conf = 1;
    var reasons = [];

    if (cov > 0.90) { conf -= 0.7; reasons.push('≈full frame (removal likely failed)'); }
    else if (cov > 0.80) { conf -= 0.3; reasons.push('high coverage'); }

    if (midRatio != null && midRatio > 0.55) {
      conf -= 0.25; reasons.push('soft/mushy alpha edge');
    }

    var bs = 2;
    var bOn = [0,0,0,0], bTot = [0,0,0,0];
    for (var x = 0; x < W; x++) {
      for (var y = 0; y < bs; y++) { bTot[0]++; if (mask[y*W + x]) bOn[0]++; }
      for (var y2 = H - bs; y2 < H; y2++) { bTot[1]++; if (mask[y2*W + x]) bOn[1]++; }
    }
    for (var yy = 0; yy < H; yy++) {
      for (var xx = 0; xx < bs; xx++) { bTot[2]++; if (mask[yy*W + xx]) bOn[2]++; }
      for (var xx2 = W - bs; xx2 < W; xx2++) { bTot[3]++; if (mask[yy*W + xx2]) bOn[3]++; }
    }
    var minBorder = Math.min(bOn[0]/bTot[0], bOn[1]/bTot[1], bOn[2]/bTot[2], bOn[3]/bTot[3]);
    if (minBorder > 0.5) { conf -= 0.5; reasons.push('art bleeds to all edges'); }

    var tinyHit = false;
    if (cov > 0 && cov < 0.005) { conf -= 0.6; reasons.push('subject very tiny'); tinyHit = true; }
    else if (cov < 0.015 && cov > 0) { conf -= 0.4; reasons.push('subject tiny'); tinyHit = true; }

    var eroded = erodeMask(mask, W, H, GATE.erosionPx);
    var efg = 0;
    for (var k = 0; k < eroded.length; k++) if (eroded[k]) efg++;
    var erosionDrop = fg > 0 ? (fg - efg) / fg : 0;
    var erosionFired = false;
    var perimSuspect = (perimChromaStd != null && perimChromaStd > GATE.perimChromaCleanFloor);
    if (erosionDrop > GATE.erosionDropMax && perimSuspect) {
      conf -= GATE.erosionPenalty;
      reasons.push('stringy / thin-stroke matte (erosion drop ' + Math.round(erosionDrop * 100) + '%)');
      erosionFired = true;
    }

    if (perimChromaStd != null && perimChromaStd > GATE.perimChromaStdMax) {
      conf -= GATE.perimChromaPenalty;
      reasons.push('retained-bg / perim chroma std ' + Math.round(perimChromaStd * 100) + '%');
    }

    if (conf < 0) conf = 0;
    if (conf > 1) conf = 1;
    return {
      conf: conf,
      why: reasons.length ? reasons.join('; ') : 'clean matte',
      components: {
        cov:            round3(cov),
        midRatio:       midRatio != null ? round3(midRatio) : null,
        minBorder:      round3(minBorder),
        tiny:           tinyHit,
        erosionDrop:    round3(erosionDrop),
        erosionFired:   erosionFired,
        perimChromaStd: perimChromaStd != null ? round3(perimChromaStd) : null,
      },
    };
  }

  // ────────────────────────────────────────────────────────────
  // Small raster helpers (used by extractAlpha for the perim band)
  // ────────────────────────────────────────────────────────────

  // Draw any HTMLImageElement/HTMLCanvasElement into a new canvas whose
  // longest side is ≤ WORKING_MAX_CLIENT. Returns { canvas, scale } where
  // scale = workingDim / sourceDim (≤ 1). Used by every full-res canvas
  // builder so mobile canvas-memory limits never silently fail pixel reads.
  function drawCapped(src) {
    var W = src.naturalWidth || src.width;
    var H = src.naturalHeight || src.height;
    var m = Math.max(W, H) || 1;
    var scale = Math.min(1, WORKING_MAX_CLIENT / m);
    var cW = Math.max(1, Math.round(W * scale));
    var cH = Math.max(1, Math.round(H * scale));
    var c = document.createElement('canvas');
    c.width = cW; c.height = cH;
    c.getContext('2d').drawImage(src, 0, 0, cW, cH);
    return { canvas: c, scale: scale };
  }

  function binaryErode1(src, W, H) {
    var dst = new Uint8Array(W * H);
    for (var y = 0; y < H; y++) {
      for (var x = 0; x < W; x++) {
        var idx = y * W + x;
        if (!src[idx]) { dst[idx] = 0; continue; }
        if (x === 0 || x === W - 1 || y === 0 || y === H - 1) { dst[idx] = 0; continue; }
        if (!src[idx - 1] || !src[idx + 1] || !src[idx - W] || !src[idx + W]) { dst[idx] = 0; continue; }
        dst[idx] = 1;
      }
    }
    return dst;
  }

  function erodeMask(src, W, H, r) {
    var cur = src;
    for (var i = 0; i < r; i++) cur = binaryErode1(cur, W, H);
    return cur;
  }

  // ────────────────────────────────────────────────────────────
  // Silhouette + alpha detection (transparent-PNG path)
  // ────────────────────────────────────────────────────────────

  function imageHasAlpha(img) {
    var srcW = img.naturalWidth || img.width;
    var srcH = img.naturalHeight || img.height;
    if (!srcW || !srcH) {
      debug('imageHasAlpha → false (zero dims)');
      return false;
    }
    var capped = drawCapped(img);
    var c  = capped.canvas;
    var W  = c.width;
    var H  = c.height;
    var cx = c.getContext('2d');
    try {
      var data = cx.getImageData(0, 0, W, H).data;
      var samples = [
        [0, 0], [W - 1, 0], [0, H - 1], [W - 1, H - 1],
        [W >> 1, 0], [0, H >> 1], [W - 1, H >> 1], [W >> 1, H - 1],
        [W >> 1, H >> 1]
      ];
      for (var i = 0; i < samples.length; i++) {
        var sx = samples[i][0], sy = samples[i][1];
        var idx = (sy * W + sx) * 4 + 3;
        if (data[idx] < 250) {
          debug('imageHasAlpha → true', { srcW: srcW, srcH: srcH, capW: W, capH: H, scale: round3(capped.scale) });
          return true;
        }
      }
    } catch (e) {
      debug('imageHasAlpha → false (getImageData threw)', e && e.message);
      return false;
    }
    debug('imageHasAlpha → false', { srcW: srcW, srcH: srcH, capW: W, capH: H, scale: round3(capped.scale) });
    return false;
  }

  // Build a canvas whose alpha is the user's alpha — but RGB is forced opaque
  // white. This is the trace source for transparent-PNG uploads: extractAlpha
  // reads pure-alpha silhouette, and the server receives the same silhouette
  // as a PNG (toDataURL preserves alpha). Built on a capped canvas so mobile
  // doesn't blow the canvas-memory ceiling.
  function buildSilhouette(img) {
    var capped = drawCapped(img);
    var c  = capped.canvas;
    var W  = c.width;
    var H  = c.height;
    var cx = c.getContext('2d');
    cx.globalCompositeOperation = 'source-in';
    cx.fillStyle = '#ffffff';
    cx.fillRect(0, 0, W, H);
    Object.defineProperty(c, 'naturalWidth',  { value: W, configurable: true });
    Object.defineProperty(c, 'naturalHeight', { value: H, configurable: true });
    debug('buildSilhouette', { W: W, H: H, scale: round3(capped.scale) });
    return c;
  }

  // ────────────────────────────────────────────────────────────
  // Rendering
  // ────────────────────────────────────────────────────────────

  // R10 — DPR-aware backing store. Drawing math stays in 0..CANVAS_SIZE
  // logical units; the backing is set to (cssSize × DPR) so each backing
  // pixel maps 1:1 to a device pixel on Retina. ctx.setTransform re-applies
  // the scale on every render (canvas.width changes reset context state).
  function applyCanvasDPR() {
    var cssRect = canvas.getBoundingClientRect();
    var cssW = Math.max(1, cssRect.width  || CANVAS_SIZE);
    var cssH = Math.max(1, cssRect.height || CANVAS_SIZE);
    var dpr  = Math.min(window.devicePixelRatio || 1, 3);
    var backingW = Math.max(1, Math.round(cssW * dpr));
    var backingH = Math.max(1, Math.round(cssH * dpr));
    if (canvas.width !== backingW || canvas.height !== backingH) {
      canvas.width  = backingW;
      canvas.height = backingH;
      debug('canvas', {
        cssW: round3(cssW), cssH: round3(cssH),
        dpr: round3(dpr),
        backingW: backingW, backingH: backingH,
      });
    }
    ctx.setTransform(backingW / CANVAS_SIZE, 0, 0, backingH / CANVAS_SIZE, 0, 0);
  }

  function renderFlatFirst() {
    // Paint the upload at honest-flat scale into the (currently hidden) canvas
    // so it's ready the moment the stepper hands off on 'done'. The canvas
    // stays display:none while the stepper is up — applyCanvasDPR() can't
    // measure layout when display:none, but the trailing render() in
    // resetStepper(revealCanvas:true) repaints once the canvas has dims.
    state.decision = 'flat';
    render();
  }

  function render() {
    if (!state.img) { showEmpty(); return; }
    applyCanvasDPR();
    var S = CANVAS_SIZE;
    ctx.clearRect(0, 0, S, S);

    if (state.controls.bg && state.controls.bg !== 'none') {
      ctx.fillStyle = state.controls.bg;
      ctx.fillRect(0, 0, S, S);
    }

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    var shape = state.controls.shape;
    if (shape === 'custom') {
      if (state.paths && state.decision === 'contour') {
        drawCustomContour();
      } else {
        drawFlat();
      }
    } else {
      drawGeometric(shape);
    }
  }

  // ── Custom shape — server path d-string → Path2D, white bleed + dashed cutline + art on top ──
  function drawCustomContour() {
    var S = CANVAS_SIZE;
    var matte = state.matteImg;
    var W = state.paths.w || matte.naturalWidth || matte.width;
    var H = state.paths.h || matte.naturalHeight || matte.height;
    var pad = S * 0.08;
    var sc = Math.min((S - 2 * pad) / W, (S - 2 * pad) / H);
    var drawW = W * sc, drawH = H * sc;
    var dx = (S - drawW) / 2, dy = (S - drawH) / 2;

    // Select the right path d-string for current bleed width.
    var key = bleedKey(state.controls.bleed);
    var slice = state.paths[key];
    if (!slice || !slice.d) {
      // Server returned no usable path for this width — degrade to flat.
      drawFlat();
      return;
    }

    // Build a transformed Path2D in canvas-coord space. Using addPath + DOMMatrix
    // (instead of ctx.scale before fill/stroke) keeps shadowBlur and lineWidth
    // in canvas-pixel units — scaling them with the path would scale the
    // shadow/stroke too, which is wrong.
    var raw = new Path2D(slice.d);
    var path = new Path2D();
    var matrix;
    if (typeof DOMMatrix !== 'undefined') {
      matrix = new DOMMatrix().translateSelf(dx, dy).scaleSelf(sc, sc);
    } else {
      // SVGMatrix fallback for older browsers.
      matrix = document.createElementNS('http://www.w3.org/2000/svg', 'svg').createSVGMatrix();
      matrix = matrix.translate(dx, dy).scaleNonUniform(sc, sc);
    }
    path.addPath(raw, matrix);

    // R9 — clip art to the polygon AND draw the matte (not the original) on
    // the Cloudinary path. The matte has transparency where the bg was
    // removed, so the white bleed shows through. Transparent-PNG path uses
    // state.img directly because the user's own alpha provides transparency.
    var artSrc = state.used_cloudinary ? state.matteImg : state.img;

    // M1 Brief 2 (revised) — for animated materials, the die-cut backing
    // IS the material, so we defer the entire layered stack to
    // paintMaterialFrame() rather than fill white. The art-fidelity rule
    // still holds: the customer's pixels are drawn on top untouched, and
    // SHEEN_ENABLED = false reverts to the vinyl branch byte-for-byte.
    if (SHEEN_ENABLED && materialIsAnimated(material)) {
      state.sheenCtx = {
        kind:        'contour',
        backingPath: path,    // material backing fills the cut path
        clipPath:    path,    // art clips to the cut path
        facePath:    path,    // face sheen clips to the cut path (border + face shimmer together)
        cutlinePath: path,
        regionX: dx, regionY: dy, regionW: drawW, regionH: drawH,
        artSrc:  artSrc,
        artX: dx, artY: dy, artW: drawW, artH: drawH,
      };
      paintMaterialFrame(state.sheenCtx, 0);
      startSheenLoop();
      return;
    }

    // Non-animated flow (vinyl + clear) — unchanged from Brief 1's Live.
    // Soft drop shadow behind the white bleed.
    // Clear substrate: the bleed border is white ink printed on transparent
    // film, so we render it at low opacity to let the page background show
    // through (faithful to the material). Shadow is lightened in lockstep
    // — a transparent sticker casts a fainter shadow. Art pixels inside the
    // cut path are unchanged (fidelity rule).
    var bleedAlpha    = (material === 'clear') ? 0.12 : 1;
    var shadowAlphaCC = (material === 'clear') ? 0.16 : 0.30;
    ctx.save();
    ctx.shadowColor   = 'rgba(0,0,0,' + shadowAlphaCC + ')';
    ctx.shadowBlur    = 26;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 10;
    ctx.fillStyle = 'rgba(255,255,255,' + bleedAlpha + ')';
    ctx.fill(path);
    ctx.restore();

    ctx.save();
    ctx.clip(path);
    ctx.drawImage(artSrc, dx, dy, drawW, drawH);
    ctx.restore();

    // Dashed cutline overlay.
    ctx.save();
    ctx.strokeStyle = CUTLINE.color;
    ctx.setLineDash(CUTLINE.dash);
    ctx.lineWidth = CUTLINE.width;
    ctx.stroke(path);
    ctx.restore();
  }

  // ── Honest flat — art + soft shadow, no border, no fake backing ──
  function drawFlat() {
    var S = CANVAS_SIZE;
    var img = state.img;
    var iw = img.naturalWidth || img.width;
    var ih = img.naturalHeight || img.height;
    if (!iw || !ih) return;
    var pad = S * 0.10;
    var sc = Math.min((S - 2 * pad) / iw, (S - 2 * pad) / ih);
    var dw = iw * sc, dh = ih * sc;
    var dx = (S - dw) / 2, dy = (S - dh) / 2;

    // M1 Brief 2 (revised) — a specialty material is real even when the
    // trace gate falls back to flat. The "die-cut" here is the art rect
    // itself (no separate border, no cutline) — we route through
    // paintMaterialFrame so the material backing + face sheen layer over
    // the art the same way as the contour/geometric paths.
    if (SHEEN_ENABLED && materialIsAnimated(material)) {
      var flatPath = new Path2D();
      flatPath.rect(dx, dy, dw, dh);
      state.sheenCtx = {
        kind:        'flat',
        backingPath: flatPath,
        clipPath:    flatPath,
        facePath:    flatPath,
        cutlinePath: null,
        regionX: dx, regionY: dy, regionW: dw, regionH: dh,
        artSrc:  img,
        artX: dx, artY: dy, artW: dw, artH: dh,
      };
      paintMaterialFrame(state.sheenCtx, 0);
      startSheenLoop();
      return;
    }

    // Non-animated flow (vinyl + clear) — unchanged.
    ctx.save();
    ctx.shadowColor   = 'rgba(0,0,0,0.45)';
    ctx.shadowBlur    = 36;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 12;
    ctx.drawImage(img, dx, dy, dw, dh);
    ctx.restore();
  }

  // ── Geometric — decoupled from art bounding box ──
  function drawGeometric(shape) {
    var S = CANVAS_SIZE;
    var img = state.img;
    var iw = img.naturalWidth || img.width;
    var ih = img.naturalHeight || img.height;
    if (!iw || !ih) return;

    var bleedPx = S * state.controls.bleed;
    var base = S * SHAPE_PROP;

    var sw, sh;
    if (shape === 'circle') { sw = sh = base; }
    else if (shape === 'square') { sw = sh = base; }
    else if (shape === 'oval') { sw = base; sh = base * 0.64; } // true ellipse (added 2026-07-08 for the free-sample shape selector)
    else { sw = base; sh = base / 1.5; }

    var ow = sw + 2 * bleedPx, oh = sh + 2 * bleedPx;
    var ox = (S - ow) / 2, oy = (S - oh) / 2;
    var ix = ox + bleedPx,  iy = oy + bleedPx;

    var cr = (shape === 'square' || shape === 'rect')
      ? Math.min(ow, oh) * (state.controls.round || 0)
      : 0;

    var outerPath = new Path2D();
    if (shape === 'circle') {
      outerPath.arc(ox + ow / 2, oy + oh / 2, ow / 2, 0, Math.PI * 2);
    } else if (shape === 'oval') {
      outerPath.ellipse(ox + ow / 2, oy + oh / 2, ow / 2, oh / 2, 0, 0, Math.PI * 2);
    } else {
      roundRectPath(outerPath, ox, oy, ow, oh, cr);
    }

    var innerCr = (shape === 'square' || shape === 'rect')
      ? Math.max(0, cr - bleedPx)
      : 0;
    var innerPath = new Path2D();
    if (shape === 'circle') {
      innerPath.arc(ix + sw / 2, iy + sh / 2, sw / 2, 0, Math.PI * 2);
    } else if (shape === 'oval') {
      innerPath.ellipse(ix + sw / 2, iy + sh / 2, sw / 2, sh / 2, 0, 0, Math.PI * 2);
    } else {
      roundRectPath(innerPath, ix, iy, sw, sh, innerCr);
    }

    // Art placement (shared between animated + non-animated branches).
    var fit = state.controls.fit;
    var scale;
    if (fit === 'fill') {
      scale = Math.max(sw / iw, sh / ih);
    } else {
      scale = Math.min(sw / iw, sh / ih);
    }
    var dw = iw * scale, dh = ih * scale;
    var dx = ix + sw / 2 - dw / 2;
    var dy = iy + sh / 2 - dh / 2;

    // M1 Brief 2 (revised) — animated materials defer the whole stack to
    // paintMaterialFrame. Backing fills outerPath (so the bleed border shows
    // the finish); face sheen clips to outerPath (so the border shimmers
    // with the face); art clips to innerPath (geometric shape inside the
    // bleed). Cutline still strokes outerPath.
    if (SHEEN_ENABLED && materialIsAnimated(material)) {
      state.sheenCtx = {
        kind:        'geometric',
        backingPath: outerPath,
        clipPath:    innerPath,
        facePath:    outerPath,
        cutlinePath: outerPath,
        regionX: ox, regionY: oy, regionW: ow, regionH: oh,
        artSrc:  img,
        artX: dx, artY: dy, artW: dw, artH: dh,
      };
      paintMaterialFrame(state.sheenCtx, 0);
      startSheenLoop();
      return;
    }

    // Non-animated flow (vinyl + clear) — unchanged from Brief 1's Live.
    // Clear substrate: translucent white bleed + lighter shadow, mirroring
    // drawCustomContour(). Same fidelity reasoning (white ink on clear film).
    var bleedAlphaG  = (material === 'clear') ? 0.12 : 1;
    var shadowAlphaG = (material === 'clear') ? 0.16 : 0.30;
    ctx.save();
    ctx.shadowColor   = 'rgba(0,0,0,' + shadowAlphaG + ')';
    ctx.shadowBlur    = 26;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 10;
    ctx.fillStyle = 'rgba(255,255,255,' + bleedAlphaG + ')';
    ctx.fill(outerPath);
    ctx.restore();

    ctx.save();
    ctx.clip(innerPath);
    ctx.drawImage(img, dx, dy, dw, dh);
    ctx.restore();

    ctx.save();
    ctx.strokeStyle = CUTLINE.color;
    ctx.setLineDash(CUTLINE.dash);
    ctx.lineWidth = CUTLINE.width;
    ctx.stroke(outerPath);
    ctx.restore();
  }

  function roundRectPath(path, x, y, w, h, r) {
    var rr = Math.max(0, Math.min(r, Math.min(w, h) / 2));
    path.moveTo(x + rr, y);
    path.arcTo(x + w, y,     x + w, y + h, rr);
    path.arcTo(x + w, y + h, x,     y + h, rr);
    path.arcTo(x,     y + h, x,     y,     rr);
    path.arcTo(x,     y,     x + w, y,     rr);
    path.closePath();
  }

  // ────────────────────────────────────────────────────────────
  // Controls UI
  // ────────────────────────────────────────────────────────────

  function setupControls() {
    if (!controls) return;
    bindOptRow(controls.querySelector('[data-stk-preview-shape]'), 'shape', null);
    bindOptRow(controls.querySelector('[data-stk-preview-bleed]'), 'bleed', parseFloat);
    bindOptRow(controls.querySelector('[data-stk-preview-round]'), 'round', parseFloat);
    bindOptRow(controls.querySelector('[data-stk-preview-fit]'),   'fit',   null);
    bindSwRow(controls.querySelector('[data-stk-preview-bg]'),     'bg');
    updateRoundRowAvailability();
    updateFitRowAvailability();
  }

  function bindOptRow(row, key, parse) {
    if (!row) return;
    row.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-v]');
      if (!btn || btn.disabled) return;
      var kids = row.children;
      for (var i = 0; i < kids.length; i++) kids[i].classList.remove('is-selected');
      btn.classList.add('is-selected');
      state.controls[key] = parse ? parse(btn.dataset.v) : btn.dataset.v;
      if (key === 'shape') {
        updateRoundRowAvailability();
        updateFitRowAvailability();
        if (state.img) {
          if (state.controls.shape === 'custom') {
            state.decision = state.paths ? 'contour' : 'flat';
          } else {
            state.decision = 'geometric';
          }
        }
      }
      // Bleed change is instant — paths are pre-fetched for all 3 widths.
      // No /api/trace re-call (this is the whole point of Round-14's single
      // batched response).
      if (state.img) render();
      notify();
    });
  }

  function bindSwRow(row, key) {
    if (!row) return;
    row.addEventListener('click', function (e) {
      var sw = e.target.closest('[data-v]');
      if (!sw) return;
      var kids = row.children;
      for (var i = 0; i < kids.length; i++) kids[i].classList.remove('is-selected');
      sw.classList.add('is-selected');
      state.controls[key] = sw.dataset.v;
      if (state.img) render();
      notify();
    });
  }

  // Geo-only controls (Round, Fit/Fill) hide entirely under shape=custom
  // rather than sit greyed. The wrapper carries the label, so hide the
  // wrapper (.stk__preview-ctrl) — not just the options row. `disabled` is
  // kept as a defensive fallback in case the hidden attribute is overridden.
  function updateRoundRowAvailability() {
    if (!controls) return;
    var row = controls.querySelector('[data-stk-preview-round]');
    if (!row) return;
    var wrap = row.closest('.stk__preview-ctrl') || row;
    var on = state.controls.shape === 'square' || state.controls.shape === 'rect';
    wrap.hidden = !on;
    var kids = row.children;
    for (var i = 0; i < kids.length; i++) kids[i].disabled = !on;
  }

  function updateFitRowAvailability() {
    if (!controls) return;
    var row = controls.querySelector('[data-stk-preview-fit]');
    if (!row) return;
    var wrap = row.closest('.stk__preview-ctrl') || row;
    var on = state.controls.shape !== 'custom';
    wrap.hidden = !on;
    var kids = row.children;
    for (var i = 0; i < kids.length; i++) kids[i].disabled = !on;
  }

  // ────────────────────────────────────────────────────────────
  // Expose
  // ────────────────────────────────────────────────────────────

  window.TDStickerPreview = { init: init, uploadArtwork: uploadArtwork };

})();
